import React, { useState, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { Toaster, toast } from 'sonner';
import "@/App.css";

import { LuoPanCompass } from './components/compass/LuoPanCompass';
import { FloorPlanCanvas } from './components/canvas/FloorPlanCanvas';
import { ControlPanel } from './components/ControlPanel';
import PricingPage from './pages/PricingPage';
import { Compass, Upload, RotateCw, Download, Settings2, Plus, Lock, Unlock, Trash2, Star, DoorOpen, Navigation, Crosshair, ZoomIn, ZoomOut, RotateCcw, X, GripVertical, User, LogOut, Shield, Maximize2, Undo2, Redo2 } from 'lucide-react';
import { calculateKuaNumber, getKuaGroup, getKuaDirections } from './utils/kuaCalculator';
import { useAuth } from './contexts/AuthContext';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { AdminDashboard } from './pages/AdminDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate unique ID for each compass
const generateId = () => `compass-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

function App() {
  // Auth state
  const { user, isAuthenticated, isAdmin, logout, loading: authLoading } = useAuth();
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'login', 'register', 'admin', 'pricing'
  const [isPlotterMode, setIsPlotterMode] = useState(false); // Track if user entered plotter
  const isPro = user?.tier === 'PRO';

  // PRO feature gate helper
  const requirePro = useCallback((featureName) => {
    if (isPro) return true;
    toast.error(`${featureName} is a PRO feature`, {
      description: 'Upgrade to PRO to unlock all features',
      action: { label: 'Upgrade', onClick: () => setCurrentPage('pricing') },
      duration: 4000,
    });
    return false;
  }, [isPro]);
  
  // Multiple compasses state
  const [compasses, setCompasses] = useState([]);
  const [selectedCompassId, setSelectedCompassId] = useState(null);
  
  // Mobile panel toggles
  const [leftPanelExpanded, setLeftPanelExpanded] = useState(false);
  const [rightPanelExpanded, setRightPanelExpanded] = useState(false);
  
  // Main Door state
  const [mainDoor, setMainDoor] = useState(null);
  const [isDoorSelected, setIsDoorSelected] = useState(false);
  const [isMovingDoor, setIsMovingDoor] = useState(false);
  const [doorMoveStart, setDoorMoveStart] = useState({ x: 0, y: 0 });
  const [isRotatingDoor, setIsRotatingDoor] = useState(false);
  const [doorRotateStart, setDoorRotateStart] = useState({ angle: 0, cx: 0, cy: 0 });
  const [isResizingDoor, setIsResizingDoor] = useState(false);
  const [doorResizeStart, setDoorResizeStart] = useState({ size: 40, y: 0 });
  const [doorFacingDegree, setDoorFacingDegree] = useState('');
  
  // North Marker state
  const [northMarker, setNorthMarker] = useState(null);
  const [isMovingNorth, setIsMovingNorth] = useState(false);
  const [northMoveStart, setNorthMoveStart] = useState({ x: 0, y: 0 });
  
  // Water Element state
  const [waterElement, setWaterElement] = useState(null);
  const [isMovingWater, setIsMovingWater] = useState(false);
  const [waterMoveStart, setWaterMoveStart] = useState({ x: 0, y: 0 });
  
  // Wood Element state
  const [woodElement, setWoodElement] = useState(null);
  const [isMovingWood, setIsMovingWood] = useState(false);
  const [woodMoveStart, setWoodMoveStart] = useState({ x: 0, y: 0 });
  
  // Fire Element state
  const [fireElement, setFireElement] = useState(null);
  const [isMovingFire, setIsMovingFire] = useState(false);
  const [fireMoveStart, setFireMoveStart] = useState({ x: 0, y: 0 });
  
  // Earth Element state
  const [earthElement, setEarthElement] = useState(null);
  const [isMovingEarth, setIsMovingEarth] = useState(false);
  const [earthMoveStart, setEarthMoveStart] = useState({ x: 0, y: 0 });
  
  // Metal Element state
  const [metalElement, setMetalElement] = useState(null);
  const [isMovingMetal, setIsMovingMetal] = useState(false);
  const [metalMoveStart, setMetalMoveStart] = useState({ x: 0, y: 0 });
  
  // Multiple Element Icons state (for adding multiple instances)
  const [elementIcons, setElementIcons] = useState([]);
  const [movingIconId, setMovingIconId] = useState(null);
  const [iconMoveStart, setIconMoveStart] = useState({ x: 0, y: 0 });
  const [rotatingIconId, setRotatingIconId] = useState(null);
  const [iconRotateStart, setIconRotateStart] = useState({ angle: 0, cx: 0, cy: 0 });
  const [resizingIconId, setResizingIconId] = useState(null);
  const [iconResizeStart, setIconResizeStart] = useState({ size: 50, y: 0 });
  
  // Text-only Element Labels state
  const [textLabels, setTextLabels] = useState([]);
  const [movingLabelId, setMovingLabelId] = useState(null);
  const [labelMoveStart, setLabelMoveStart] = useState({ x: 0, y: 0 });
  
  // Floor Plan rotation state
  const [floorPlanRotation, setFloorPlanRotation] = useState(0);
  
  // Display mode: '8dir' for 8 directions, '24mtn' for 24 mountains
  const [directionDisplayMode, setDirectionDisplayMode] = useState('8dir');
  
  // Global settings
  const [showCenterLines, setShowCenterLines] = useState(true);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Center box drawing state
  const [isDrawingCenterBox, setIsDrawingCenterBox] = useState(false);
  const [centerBox, setCenterBox] = useState(null);  // Temporary box while drawing/confirming
  const [confirmedBox, setConfirmedBox] = useState(null);  // Confirmed box coordinates (for the X lines)
  
  // Floor plan zoom state (lifted from FloorPlanCanvas)
  const [floorPlanZoom, setFloorPlanZoom] = useState(100);
  const [isFloorPlanLocked, setIsFloorPlanLocked] = useState(false);
  const [floorPlanOffset, setFloorPlanOffset] = useState({ x: 0, y: 0 });
  const [initialDoorOffset, setInitialDoorOffset] = useState(null); // Stores door position when locked
  
  // Personal Kua Analysis state
  const [kuaAnalysisEnabled, setKuaAnalysisEnabled] = useState(false);
  const [birthDate, setBirthDate] = useState('1990-01-15');
  const [gender, setGender] = useState('male');
  const [kuaNumber, setKuaNumber] = useState(null);
  const [kuaDirections, setKuaDirections] = useState(null);
  
  // Dragging state
  const [movingCompassId, setMovingCompassId] = useState(null);
  const [moveStart, setMoveStart] = useState({ x: 0, y: 0 });

  // Panel position state (for draggable panel)
  const [panelPosition, setPanelPosition] = useState({ x: 16, y: 60 });
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [panelDragStart, setPanelDragStart] = useState({ x: 0, y: 0 });

  // Save dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  // Refs
  const canvasRef = useRef(null);
  const exportRef = useRef(null);
  const floorPlanRef = useRef(null);

  // Transform screen coordinates to canvas coordinates (accounting for floor plan rotation)
  const screenToCanvas = useCallback((clientX, clientY) => {
    if (!floorPlanRotation || !exportRef.current) return { x: clientX, y: clientY };
    const rect = exportRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    const rad = (-floorPlanRotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
  }, [floorPlanRotation]);

  // Undo/Redo history system
  const historyRef = useRef([]);
  const futureRef = useRef([]);
  const isRestoringRef = useRef(false);
  const lastSnapshotRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Capture current state as a snapshot (excludes UI-only state like panel positions)
  const getSnapshot = useCallback(() => JSON.stringify({
    compasses,
    selectedCompassId,
    mainDoor,
    elementIcons,
    textLabels,
    floorPlanRotation,
    confirmedBox,
    centerBox,
    showCenterLines,
    doorFacingDegree,
    waterElement,
    woodElement,
    fireElement,
    earthElement,
    metalElement,
    northMarker,
    kuaAnalysisEnabled,
    floorPlanZoom,
    floorPlanOffset,
  }), [compasses, selectedCompassId, mainDoor, elementIcons, textLabels, floorPlanRotation,
    confirmedBox, centerBox, showCenterLines, doorFacingDegree, waterElement, woodElement,
    fireElement, earthElement, metalElement, northMarker, kuaAnalysisEnabled, floorPlanZoom, floorPlanOffset]);

  // Restore a snapshot
  const restoreSnapshot = useCallback((snapshotStr) => {
    isRestoringRef.current = true;
    const s = JSON.parse(snapshotStr);
    setCompasses(s.compasses || []);
    setSelectedCompassId(s.selectedCompassId || null);
    setMainDoor(s.mainDoor || null);
    setElementIcons(s.elementIcons || []);
    setTextLabels(s.textLabels || []);
    setFloorPlanRotation(s.floorPlanRotation || 0);
    setConfirmedBox(s.confirmedBox || null);
    setCenterBox(s.centerBox || null);
    setShowCenterLines(s.showCenterLines ?? true);
    setDoorFacingDegree(s.doorFacingDegree || '');
    setWaterElement(s.waterElement || null);
    setWoodElement(s.woodElement || null);
    setFireElement(s.fireElement || null);
    setEarthElement(s.earthElement || null);
    setMetalElement(s.metalElement || null);
    setNorthMarker(s.northMarker || null);
    setKuaAnalysisEnabled(s.kuaAnalysisEnabled || false);
    setFloorPlanZoom(s.floorPlanZoom || 100);
    setFloorPlanOffset(s.floorPlanOffset || { x: 0, y: 0 });
  }, []);

  // Auto-track state changes with debounce
  React.useEffect(() => {
    if (!imageLoaded && !isPlotterMode) return;

    const currentSnapshot = getSnapshot();

    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      lastSnapshotRef.current = currentSnapshot;
      return;
    }

    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (lastSnapshotRef.current && currentSnapshot !== lastSnapshotRef.current) {
        historyRef.current.push(lastSnapshotRef.current);
        if (historyRef.current.length > 50) historyRef.current.shift();
        futureRef.current = [];
        setCanUndo(true);
        setCanRedo(false);
      }
      lastSnapshotRef.current = currentSnapshot;
    }, 400);
  }, [getSnapshot, imageLoaded, isPlotterMode]);

  // Undo action
  const handleUndo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const currentSnapshot = lastSnapshotRef.current || getSnapshot();
    futureRef.current.push(currentSnapshot);
    const prevSnapshot = historyRef.current.pop();
    lastSnapshotRef.current = prevSnapshot;
    restoreSnapshot(prevSnapshot);
    setCanUndo(historyRef.current.length > 0);
    setCanRedo(true);
  }, [getSnapshot, restoreSnapshot]);

  // Redo action
  const handleRedo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const currentSnapshot = lastSnapshotRef.current || getSnapshot();
    historyRef.current.push(currentSnapshot);
    const nextSnapshot = futureRef.current.pop();
    lastSnapshotRef.current = nextSnapshot;
    restoreSnapshot(nextSnapshot);
    setCanUndo(true);
    setCanRedo(futureRef.current.length > 0);
  }, [getSnapshot, restoreSnapshot]);

  // Keyboard shortcuts: Ctrl+Z (undo), Ctrl+Shift+Z / Ctrl+Y (redo)
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Get selected compass
  const selectedCompass = compasses.find(c => c.id === selectedCompassId);

  // Handle image load - create first compass
  const handleImageLoad = useCallback((size) => {
    // If size is null, image was cleared
    if (size === null) {
      // Don't set imageLoaded to false - keep dashboard visible
      // Just clear compasses since floor plan is gone
      return;
    }
    
    setImageLoaded(true);
    if (canvasRef.current) {
      const containerWidth = canvasRef.current.clientWidth;
      const containerHeight = canvasRef.current.clientHeight;
      const newCompass = {
        id: generateId(),
        position: { x: containerWidth / 2, y: containerHeight / 2 },
        rotation: 0,
        size: 550,
        opacity: 0.75,
        isLocked: false,
        isVisible: true,
        showFlyingStar: false,
        flyingStarScale: 1,
        highlightedDirections: [],
        showCompassVisual: true, // When false, only torch light shows
      };
      setCompasses([newCompass]);
      setSelectedCompassId(newCompass.id);
    }
  }, []);

  // Add new compass
  const handleAddCompass = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    // Offset new compass slightly from center
    const offset = compasses.length * 50;
    const newCompass = {
      id: generateId(),
      position: { x: containerWidth / 2 + offset, y: containerHeight / 2 + offset },
      rotation: 0,
      size: 450,
      opacity: 0.75,
      isLocked: false,
      isVisible: true,
      showFlyingStar: false,
      flyingStarScale: 1,
      highlightedDirections: [],
      showCompassVisual: true,
    };
    setCompasses(prev => [...prev, newCompass]);
    setSelectedCompassId(newCompass.id);
    toast.success('New compass added!');
  }, [compasses.length]);

  // Delete compass
  const handleDeleteCompass = useCallback((id) => {
    setCompasses(prev => prev.filter(c => c.id !== id));
    if (selectedCompassId === id) {
      setSelectedCompassId(compasses.length > 1 ? compasses[0].id : null);
    }
    toast.success('Compass removed');
  }, [selectedCompassId, compasses]);

  // Toggle lock for a compass
  const handleToggleLock = useCallback((id) => {
    setCompasses(prev => prev.map(c => 
      c.id === id ? { ...c, isLocked: !c.isLocked } : c
    ));
    const compass = compasses.find(c => c.id === id);
    toast.success(compass?.isLocked ? 'Compass unlocked' : 'Compass locked');
  }, [compasses]);

  // Center compass to middle of canvas (or to confirmed box center if available)
  const handleCenterCompass = useCallback((id) => {
    if (!canvasRef.current) return;
    
    // Get canvas dimensions with fallback for mobile
    const canvasWidth = canvasRef.current.clientWidth || canvasRef.current.offsetWidth || window.innerWidth;
    const canvasHeight = canvasRef.current.clientHeight || canvasRef.current.offsetHeight || window.innerHeight;
    
    let targetX, targetY;
    
    if (confirmedBox && floorPlanRef.current) {
      // Use the confirmed box center - convert from image coords to canvas coords
      const transform = floorPlanRef.current.getTransform();
      if (transform && transform.scale && transform.offset) {
        const { scale, offset } = transform;
        // Convert image coordinates to canvas coordinates
        targetX = offset.x + confirmedBox.centerX * scale;
        targetY = offset.y + confirmedBox.centerY * scale;
      } else {
        // Fallback to canvas center
        targetX = canvasWidth / 2;
        targetY = canvasHeight / 2;
      }
    } else {
      // Default: center of canvas
      targetX = canvasWidth / 2;
      targetY = canvasHeight / 2;
    }
    
    // Ensure valid coordinates
    if (isNaN(targetX) || isNaN(targetY) || targetX <= 0 || targetY <= 0) {
      targetX = canvasWidth / 2;
      targetY = canvasHeight / 2;
    }
    
    setCompasses(prev => prev.map(c => 
      c.id === id ? { 
        ...c, 
        position: { x: targetX, y: targetY } 
      } : c
    ));
    toast.success(confirmedBox ? 'Compass snapped to custom center!' : 'Compass centered!');
  }, [confirmedBox]);

  // Add Main Door
  const handleAddMainDoor = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    setMainDoor({
      position: { x: containerWidth / 2, y: containerHeight - 100 },
      rotation: 0,
      opacity: 1,
      size: 40,
      isLocked: false,
    });
    toast.success('Main Door marker added!');
  }, []);

  // Handle floor plan offset change - store the offset for positioning locked markers
  const handleFloorPlanOffsetChange = useCallback((newOffset) => {
    setFloorPlanOffset(newOffset);
  }, []);

  // When door is locked, store its position relative to floor plan offset
  // When door lock state changes
  const handleToggleDoorLock = useCallback(() => {
    setMainDoor(prev => {
      if (!prev) return prev;
      const newLockState = !prev.isLocked;
      
      if (newLockState) {
        // Door is being locked - store the current relative position
        setInitialDoorOffset({
          x: prev.position.x - floorPlanOffset.x,
          y: prev.position.y - floorPlanOffset.y,
        });
        toast.success('Main Door locked! It will move with floor plan.');
      } else {
        // Door is being unlocked
        setInitialDoorOffset(null);
        toast.success('Main Door unlocked');
      }
      
      return { ...prev, isLocked: newLockState };
    });
  }, [floorPlanOffset]);

  // Remove Main Door
  const handleRemoveMainDoor = useCallback(() => {
    setMainDoor(null);
    setInitialDoorOffset(null);
    toast.success('Main Door marker removed');
  }, []);

  // Reset Everything - Start Fresh (stay on dashboard)
  const handleResetAll = useCallback(() => {
    // Clear floor plan image only
    floorPlanRef.current?.clearImage();
    
    // Reset all markers and states
    setMainDoor(null);
    setInitialDoorOffset(null);
    setCompasses([]);
    setSelectedCompassId(null);
    setNorthMarker(null);
    setWaterElement(null);
    setWoodElement(null);
    setFireElement(null);
    setEarthElement(null);
    setMetalElement(null);
    setElementIcons([]);
    setTextLabels([]);
    setCenterBox(null);
    setConfirmedBox(null);
    setFloorPlanRotation(0);
    setFloorPlanOffset({ x: 0, y: 0 });
    setDoorFacingDegree('');
    // Keep imageLoaded true and isPlotterMode true so dashboard stays visible
    // The FloorPlanCanvas will show upload prompt when image is cleared
    
    toast.success('All cleared! Upload a new floor plan to start.');
  }, []);

  // Main Door drag handlers
  const handleDoorMouseDown = useCallback((e) => {
    if (!mainDoor) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Select the door when clicked/mousedown
    setIsDoorSelected(true);
    
    if (mainDoor.isLocked) return;
    
    setIsMovingDoor(true);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setDoorMoveStart({
      x: cx - mainDoor.position.x,
      y: cy - mainDoor.position.y,
    });
  }, [mainDoor, screenToCanvas]);

  // Main Door rotation handle
  const handleDoorRotateStart = useCallback((e) => {
    if (!mainDoor || mainDoor.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsRotatingDoor(true);
    
    // Use the element's screen position for rotation
    const rect = e.currentTarget.closest('.main-door-marker').getBoundingClientRect();
    const sCenterX = rect.left + rect.width / 2;
    const sCenterY = rect.top + rect.height / 2;
    const angle = Math.atan2(e.clientY - sCenterY, e.clientX - sCenterX) * (180 / Math.PI);
    setDoorRotateStart({ angle: angle - (mainDoor.rotation || 0), cx: sCenterX, cy: sCenterY });
  }, [mainDoor]);

  // Main Door resize handle
  const handleDoorResizeStart = useCallback((e) => {
    if (!mainDoor || mainDoor.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsResizingDoor(true);
    setDoorResizeStart({
      size: mainDoor.size || 40,
      y: e.clientY,
    });
  }, [mainDoor]);

  // Handle Main Door rotation change from slider
  const handleDoorRotationChange = useCallback((newRotation) => {
    if (!mainDoor || mainDoor.isLocked) return;
    setMainDoor(prev => ({
      ...prev,
      rotation: newRotation,
    }));
  }, [mainDoor]);

  // Handle Main Door opacity change
  const handleDoorOpacityChange = useCallback((newOpacity) => {
    if (!mainDoor || mainDoor.isLocked) return;
    setMainDoor(prev => ({
      ...prev,
      opacity: newOpacity,
    }));
  }, [mainDoor]);

  // Handle Main Door size change
  const handleDoorSizeChange = useCallback((newSize) => {
    if (!mainDoor || mainDoor.isLocked) return;
    setMainDoor(prev => ({
      ...prev,
      size: newSize,
    }));
  }, [mainDoor]);

  // Handle Door Facing Degree - Auto-rotate ENTIRE canvas to align door with input degree
  const handleDoorFacingDegreeChange = useCallback((inputDegree) => {
    setDoorFacingDegree(inputDegree);
    
    if (!mainDoor || !canvasRef.current || !compasses.length) return;
    
    const degree = parseFloat(inputDegree);
    if (isNaN(degree) || degree < 0 || degree > 360) return;
    
    // Get the first compass as reference
    const compass = compasses[0];
    if (!compass) return;
    
    // Calculate angle from compass center to door position
    const doorX = mainDoor.position.x;
    const doorY = mainDoor.position.y;
    const compassX = compass.position.x;
    const compassY = compass.position.y;
    
    // Calculate current angle from compass center to door (in degrees, 0 = North/top)
    const deltaX = doorX - compassX;
    const deltaY = doorY - compassY;
    let currentAngle = Math.atan2(deltaX, -deltaY) * (180 / Math.PI);
    currentAngle = ((currentAngle % 360) + 360) % 360;
    
    // Calculate rotation needed to align door with input degree
    // We rotate the entire canvas so the door appears at the input degree position on screen
    // Since North should be at top (0°), we rotate so door is at the user's input degree
    let rotationNeeded = degree - currentAngle;
    rotationNeeded = ((rotationNeeded % 360) + 360) % 360;
    
    // Apply rotation to the entire canvas (floor plan + door + compass all rotate together)
    setFloorPlanRotation(rotationNeeded);
    
    toast.success(`Aligned! Door facing ${degree}° - Everything rotated together.`);
  }, [mainDoor, compasses]);

  // Add North Marker
  const handleAddNorthMarker = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    
    setNorthMarker({
      position: { x: containerWidth / 2, y: 80 },
      isLocked: false,
    });
    toast.success('North marker added!');
  }, []);

  // Remove North Marker
  const handleRemoveNorthMarker = useCallback(() => {
    setNorthMarker(null);
    toast.success('North marker removed');
  }, []);

  // Add Water Element
  const handleAddWaterElement = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    const newIcon = {
      id: Date.now() + Math.random(),
      type: 'WATER',
      char: '水',
      color: '#2563eb',
      name: 'Water',
      position: { x: containerWidth / 2 + Math.random() * 100 - 50, y: containerHeight / 2 + Math.random() * 100 - 50 },
      size: 50,
      rotation: 0,
      isLocked: false,
    };
    setElementIcons(prev => [...prev, newIcon]);
    toast.success('Water element added!');
  }, []);

  // Remove Water Element
  const handleRemoveWaterElement = useCallback(() => {
    setWaterElement(null);
    toast.success('Water element removed');
  }, []);

  // Toggle Water Element lock
  const handleToggleWaterLock = useCallback(() => {
    if (!waterElement) return;
    setWaterElement(prev => ({
      ...prev,
      isLocked: !prev.isLocked,
    }));
    toast.success(waterElement.isLocked ? 'Water element unlocked' : 'Water element locked');
  }, [waterElement]);

  // Water Element drag handler
  const handleWaterMouseDown = useCallback((e) => {
    if (!waterElement || waterElement.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsMovingWater(true);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setWaterMoveStart({
      x: cx - waterElement.position.x,
      y: cy - waterElement.position.y,
    });
  }, [waterElement, screenToCanvas]);

  // Handle Water Element size change
  const handleWaterSizeChange = useCallback((newSize) => {
    if (!waterElement || waterElement.isLocked) return;
    setWaterElement(prev => ({
      ...prev,
      size: newSize,
    }));
  }, [waterElement]);

  // Add Wood Element
  const handleAddWoodElement = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    const newIcon = {
      id: Date.now() + Math.random(),
      type: 'WOOD',
      char: '木',
      color: '#16a34a',
      name: 'Wood',
      position: { x: containerWidth / 2 + Math.random() * 100 - 50, y: containerHeight / 2 + Math.random() * 100 - 50 },
      size: 50,
      rotation: 0,
      isLocked: false,
    };
    setElementIcons(prev => [...prev, newIcon]);
    toast.success('Wood element added!');
  }, []);

  // Remove Wood Element
  const handleRemoveWoodElement = useCallback(() => {
    setWoodElement(null);
    toast.success('Wood element removed');
  }, []);

  // Toggle Wood Element lock
  const handleToggleWoodLock = useCallback(() => {
    if (!woodElement) return;
    setWoodElement(prev => ({
      ...prev,
      isLocked: !prev.isLocked,
    }));
    toast.success(woodElement.isLocked ? 'Wood element unlocked' : 'Wood element locked');
  }, [woodElement]);

  // Wood Element drag handler
  const handleWoodMouseDown = useCallback((e) => {
    if (!woodElement || woodElement.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsMovingWood(true);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setWoodMoveStart({
      x: cx - woodElement.position.x,
      y: cy - woodElement.position.y,
    });
  }, [woodElement, screenToCanvas]);

  // Handle Wood Element size change
  const handleWoodSizeChange = useCallback((newSize) => {
    if (!woodElement || woodElement.isLocked) return;
    setWoodElement(prev => ({
      ...prev,
      size: newSize,
    }));
  }, [woodElement]);

  // Add Fire Element
  const handleAddFireElement = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    const newIcon = {
      id: Date.now() + Math.random(),
      type: 'FIRE',
      char: '火',
      color: '#dc2626',
      name: 'Fire',
      position: { x: containerWidth / 2 + Math.random() * 100 - 50, y: containerHeight / 2 + Math.random() * 100 - 50 },
      size: 50,
      rotation: 0,
      isLocked: false,
    };
    setElementIcons(prev => [...prev, newIcon]);
    toast.success('Fire element added!');
  }, []);

  // Remove Fire Element
  const handleRemoveFireElement = useCallback(() => {
    setFireElement(null);
    toast.success('Fire element removed');
  }, []);

  // Toggle Fire Element lock
  const handleToggleFireLock = useCallback(() => {
    if (!fireElement) return;
    setFireElement(prev => ({
      ...prev,
      isLocked: !prev.isLocked,
    }));
    toast.success(fireElement.isLocked ? 'Fire element unlocked' : 'Fire element locked');
  }, [fireElement]);

  // Fire Element drag handler
  const handleFireMouseDown = useCallback((e) => {
    if (!fireElement || fireElement.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsMovingFire(true);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setFireMoveStart({
      x: cx - fireElement.position.x,
      y: cy - fireElement.position.y,
    });
  }, [fireElement, screenToCanvas]);

  // Add Earth Element
  const handleAddEarthElement = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    const newIcon = {
      id: Date.now() + Math.random(),
      type: 'EARTH',
      char: '土',
      color: '#92400e',
      name: 'Earth',
      position: { x: containerWidth / 2 + Math.random() * 100 - 50, y: containerHeight / 2 + Math.random() * 100 - 50 },
      size: 50,
      rotation: 0,
      isLocked: false,
    };
    setElementIcons(prev => [...prev, newIcon]);
    toast.success('Earth element added!');
  }, []);

  // Remove Earth Element
  const handleRemoveEarthElement = useCallback(() => {
    setEarthElement(null);
    toast.success('Earth element removed');
  }, []);

  // Toggle Earth Element lock
  const handleToggleEarthLock = useCallback(() => {
    if (!earthElement) return;
    setEarthElement(prev => ({
      ...prev,
      isLocked: !prev.isLocked,
    }));
    toast.success(earthElement.isLocked ? 'Earth element unlocked' : 'Earth element locked');
  }, [earthElement]);

  // Earth Element drag handler
  const handleEarthMouseDown = useCallback((e) => {
    if (!earthElement || earthElement.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsMovingEarth(true);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setEarthMoveStart({
      x: cx - earthElement.position.x,
      y: cy - earthElement.position.y,
    });
  }, [earthElement, screenToCanvas]);

  // Add Metal Element
  const handleAddMetalElement = useCallback(() => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    const newIcon = {
      id: Date.now() + Math.random(),
      type: 'METAL',
      char: '金',
      color: '#6b7280',
      name: 'Metal',
      position: { x: containerWidth / 2 + Math.random() * 100 - 50, y: containerHeight / 2 + Math.random() * 100 - 50 },
      size: 50,
      rotation: 0,
      isLocked: false,
    };
    setElementIcons(prev => [...prev, newIcon]);
    toast.success('Metal element added!');
  }, []);

  // Remove Metal Element
  const handleRemoveMetalElement = useCallback(() => {
    setMetalElement(null);
    toast.success('Metal element removed');
  }, []);

  // Toggle Metal Element lock
  const handleToggleMetalLock = useCallback(() => {
    if (!metalElement) return;
    setMetalElement(prev => ({
      ...prev,
      isLocked: !prev.isLocked,
    }));
    toast.success(metalElement.isLocked ? 'Metal element unlocked' : 'Metal element locked');
  }, [metalElement]);

  // Metal Element drag handler
  const handleMetalMouseDown = useCallback((e) => {
    if (!metalElement || metalElement.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsMovingMetal(true);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setMetalMoveStart({
      x: cx - metalElement.position.x,
      y: cy - metalElement.position.y,
    });
  }, [metalElement, screenToCanvas]);

  // Text Label handlers
  const handleAddTextLabel = useCallback((elementType) => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    const colors = {
      WATER: '#2563eb',
      WOOD: '#16a34a',
      FIRE: '#dc2626',
      EARTH: '#92400e',
      METAL: '#6b7280'
    };
    
    const newLabel = {
      id: Date.now(),
      text: elementType,
      position: { x: containerWidth / 2 + Math.random() * 100 - 50, y: containerHeight / 2 + Math.random() * 100 - 50 },
      size: 18,
      rotation: 0,
      color: colors[elementType],
      isLocked: false,
    };
    
    setTextLabels(prev => [...prev, newLabel]);
    toast.success(`${elementType} label added!`);
  }, []);

  const handleRemoveTextLabel = useCallback((id) => {
    setTextLabels(prev => prev.filter(label => label.id !== id));
    toast.success('Label removed');
  }, []);

  const handleToggleLabelLock = useCallback((id) => {
    setTextLabels(prev => prev.map(label => 
      label.id === id ? { ...label, isLocked: !label.isLocked } : label
    ));
  }, []);

  const handleLabelMouseDown = useCallback((e, label) => {
    if (label.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setMovingLabelId(label.id);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setLabelMoveStart({
      x: cx - label.position.x,
      y: cy - label.position.y,
    });
  }, [screenToCanvas]);

  const handleUpdateLabelSize = useCallback((id, size) => {
    setTextLabels(prev => prev.map(label => 
      label.id === id ? { ...label, size: parseInt(size) } : label
    ));
  }, []);

  const handleUpdateLabelRotation = useCallback((id, rotation) => {
    setTextLabels(prev => prev.map(label => 
      label.id === id ? { ...label, rotation: parseInt(rotation) } : label
    ));
  }, []);

  // Element Icons Array Handlers (for multiple instances)
  const handleAddElementIcon = useCallback((elementType) => {
    if (!canvasRef.current) return;
    const containerWidth = canvasRef.current.clientWidth;
    const containerHeight = canvasRef.current.clientHeight;
    
    const elementData = {
      WATER: { char: '水', color: '#2563eb', name: 'Water' },
      WOOD: { char: '木', color: '#16a34a', name: 'Wood' },
      FIRE: { char: '火', color: '#dc2626', name: 'Fire' },
      EARTH: { char: '土', color: '#92400e', name: 'Earth' },
      METAL: { char: '金', color: '#6b7280', name: 'Metal' }
    };
    
    const data = elementData[elementType];
    const newIcon = {
      id: Date.now() + Math.random(),
      type: elementType,
      char: data.char,
      color: data.color,
      name: data.name,
      position: { x: containerWidth / 2 + Math.random() * 100 - 50, y: containerHeight / 2 + Math.random() * 100 - 50 },
      size: 50,
      rotation: 0,
      isLocked: false,
    };
    
    setElementIcons(prev => [...prev, newIcon]);
    toast.success(`${data.name} icon added!`);
  }, []);

  const handleRemoveElementIcon = useCallback((id) => {
    setElementIcons(prev => prev.filter(icon => icon.id !== id));
    toast.success('Element removed');
  }, []);

  const handleToggleElementIconLock = useCallback((id) => {
    setElementIcons(prev => prev.map(icon => 
      icon.id === id ? { ...icon, isLocked: !icon.isLocked } : icon
    ));
  }, []);

  const handleElementIconMouseDown = useCallback((e, icon) => {
    if (icon.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setMovingIconId(icon.id);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setIconMoveStart({
      x: cx - icon.position.x,
      y: cy - icon.position.y,
    });
  }, [screenToCanvas]);

  const handleUpdateElementIconSize = useCallback((id, size) => {
    setElementIcons(prev => prev.map(icon => 
      icon.id === id ? { ...icon, size: parseInt(size) } : icon
    ));
  }, []);

  const handleUpdateElementIconRotation = useCallback((id, rotation) => {
    setElementIcons(prev => prev.map(icon => 
      icon.id === id ? { ...icon, rotation: parseInt(rotation) } : icon
    ));
  }, []);

  // Element Icon rotation handler (like Main Door)
  const handleIconRotateStart = useCallback((e, iconId) => {
    const icon = elementIcons.find(i => i.id === iconId);
    if (!icon || icon.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setRotatingIconId(iconId);
    // Use the element's screen position for rotation
    const rect = e.currentTarget.closest('[class$="-element-marker"]').getBoundingClientRect();
    const sCenterX = rect.left + rect.width / 2;
    const sCenterY = rect.top + rect.height / 2;
    const angle = Math.atan2(e.clientY - sCenterY, e.clientX - sCenterX) * (180 / Math.PI);
    setIconRotateStart({ angle: angle - (icon.rotation || 0), cx: sCenterX, cy: sCenterY });
  }, [elementIcons]);

  // Element Icon resize handler
  const handleIconResizeStart = useCallback((e, iconId, currentSize) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingIconId(iconId);
    setIconResizeStart({ size: currentSize || 50, y: e.clientY });
  }, []);

  // Center Box drawing handlers
  const handleStartDrawCenterBox = useCallback(() => {
    setIsDrawingCenterBox(true);
    setCenterBox(null);
    toast.info('Click and drag on the floor plan to draw the center box');
  }, []);

  const handleClearCenterBox = useCallback(() => {
    setCenterBox(null);
    setIsDrawingCenterBox(false);
    toast.success('Center box cleared');
  }, []);

  const handleClearConfirmedBox = useCallback(() => {
    setConfirmedBox(null);
    toast.success('Center box cleared - using floor plan center');
  }, []);

  const handleCenterBoxChange = useCallback((box) => {
    setCenterBox(box);
    toast.success('Box drawn! Click "Confirm" to set as new center point.');
  }, []);

  const handleConfirmCenterBox = useCallback(() => {
    if (centerBox) {
      // Save the entire box coordinates for the X lines
      setConfirmedBox({
        x: centerBox.x,
        y: centerBox.y,
        width: centerBox.width,
        height: centerBox.height,
        centerX: centerBox.x + centerBox.width / 2,
        centerY: centerBox.y + centerBox.height / 2,
      });
      setCenterBox(null);  // Hide the red dotted box
      toast.success('New center point confirmed!');
    }
  }, [centerBox]);

  const handleDrawingComplete = useCallback(() => {
    setIsDrawingCenterBox(false);
  }, []);

  // Kua Analysis handlers
  const handleKuaAnalysisToggle = useCallback((enabled) => {
    if (enabled && !isPro) { requirePro('Kua Analysis (八宅)'); return; }
    setKuaAnalysisEnabled(enabled);
    if (enabled && birthDate) {
      const kua = calculateKuaNumber(birthDate, gender);
      const directions = getKuaDirections(kua);
      setKuaNumber(kua);
      setKuaDirections(directions);
      toast.success(`Kua Number: ${kua} (${getKuaGroup(kua) === 'east' ? 'East Group 東四命' : 'West Group 西四命'})`);
    }
  }, [birthDate, gender, isPro, requirePro]);

  const handleBirthDateChange = useCallback((date) => {
    setBirthDate(date);
    if (kuaAnalysisEnabled && date) {
      const kua = calculateKuaNumber(date, gender);
      const directions = getKuaDirections(kua);
      setKuaNumber(kua);
      setKuaDirections(directions);
    }
  }, [kuaAnalysisEnabled, gender]);

  const handleGenderChange = useCallback((newGender) => {
    setGender(newGender);
    if (kuaAnalysisEnabled && birthDate) {
      const kua = calculateKuaNumber(birthDate, newGender);
      const directions = getKuaDirections(kua);
      setKuaNumber(kua);
      setKuaDirections(directions);
    }
  }, [kuaAnalysisEnabled, birthDate]);

  // North Marker drag handlers
  const handleNorthMouseDown = useCallback((e) => {
    if (!northMarker || northMarker.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsMovingNorth(true);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setNorthMoveStart({
      x: cx - northMarker.position.x,
      y: cy - northMarker.position.y,
    });
  }, [northMarker, screenToCanvas]);

  // Update compass property
  const updateCompass = useCallback((id, updates) => {
    setCompasses(prev => prev.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ));
  }, []);

  // Compass position drag handlers
  const handleCompassMouseDown = useCallback((e, compassId) => {
    const compass = compasses.find(c => c.id === compassId);
    if (!compass || compass.isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    setMovingCompassId(compassId);
    setSelectedCompassId(compassId);
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);
    setMoveStart({
      x: cx - compass.position.x,
      y: cy - compass.position.y,
    });
  }, [compasses, screenToCanvas]);

  const handleCompassMouseMove = useCallback((e) => {
    // Transform screen coordinates to canvas space (accounting for rotation)
    const { x: cx, y: cy } = screenToCanvas(e.clientX, e.clientY);

    // Handle compass movement
    if (movingCompassId) {
      const compass = compasses.find(c => c.id === movingCompassId);
      if (compass && !compass.isLocked) {
        updateCompass(movingCompassId, {
          position: {
            x: cx - moveStart.x,
            y: cy - moveStart.y,
          }
        });
      }
    }
    
    // Handle door movement
    if (isMovingDoor && mainDoor && !mainDoor.isLocked) {
      setMainDoor(prev => ({
        ...prev,
        position: {
          x: cx - doorMoveStart.x,
          y: cy - doorMoveStart.y,
        }
      }));
    }
    
    // Handle North marker movement
    if (isMovingNorth && northMarker && !northMarker.isLocked) {
      setNorthMarker(prev => ({
        ...prev,
        position: {
          x: cx - northMoveStart.x,
          y: cy - northMoveStart.y,
        }
      }));
    }
    
    // Handle Water element movement
    if (isMovingWater && waterElement && !waterElement.isLocked) {
      setWaterElement(prev => ({
        ...prev,
        position: {
          x: cx - waterMoveStart.x,
          y: cy - waterMoveStart.y,
        }
      }));
    }
    
    // Handle Wood element movement
    if (isMovingWood && woodElement && !woodElement.isLocked) {
      setWoodElement(prev => ({
        ...prev,
        position: {
          x: cx - woodMoveStart.x,
          y: cy - woodMoveStart.y,
        }
      }));
    }
    
    // Handle Fire element movement
    if (isMovingFire && fireElement && !fireElement.isLocked) {
      setFireElement(prev => ({
        ...prev,
        position: {
          x: cx - fireMoveStart.x,
          y: cy - fireMoveStart.y,
        }
      }));
    }
    
    // Handle Earth element movement
    if (isMovingEarth && earthElement && !earthElement.isLocked) {
      setEarthElement(prev => ({
        ...prev,
        position: {
          x: cx - earthMoveStart.x,
          y: cy - earthMoveStart.y,
        }
      }));
    }
    
    // Handle Metal element movement
    if (isMovingMetal && metalElement && !metalElement.isLocked) {
      setMetalElement(prev => ({
        ...prev,
        position: {
          x: cx - metalMoveStart.x,
          y: cy - metalMoveStart.y,
        }
      }));
    }
    
    // Handle Text Label movement
    if (movingLabelId) {
      setTextLabels(prev => prev.map(label => {
        if (label.id === movingLabelId && !label.isLocked) {
          return {
            ...label,
            position: {
              x: cx - labelMoveStart.x,
              y: cy - labelMoveStart.y,
            }
          };
        }
        return label;
      }));
    }
    
    // Handle Element Icon movement
    if (movingIconId) {
      setElementIcons(prev => prev.map(icon => {
        if (icon.id === movingIconId && !icon.isLocked) {
          return {
            ...icon,
            position: {
              x: cx - iconMoveStart.x,
              y: cy - iconMoveStart.y,
            }
          };
        }
        return icon;
      }));
    }
    
    // Handle Main Door rotation
    if (isRotatingDoor && mainDoor && !mainDoor.isLocked) {
      const angle = Math.atan2(e.clientY - doorRotateStart.cy, e.clientX - doorRotateStart.cx) * (180 / Math.PI);
      let newRotation = angle - doorRotateStart.angle;
      newRotation = ((newRotation % 360) + 360) % 360;
      setMainDoor(prev => ({
        ...prev,
        rotation: newRotation,
      }));
    }
    
    // Handle Main Door resize
    if (isResizingDoor && mainDoor && !mainDoor.isLocked) {
      const deltaY = doorResizeStart.y - e.clientY; // Drag up to make bigger
      const newSize = Math.max(20, Math.min(100, doorResizeStart.size + deltaY));
      setMainDoor(prev => ({
        ...prev,
        size: Math.round(newSize),
      }));
    }
    
    // Handle Element Icon rotation
    if (rotatingIconId) {
      const icon = elementIcons.find(i => i.id === rotatingIconId);
      if (icon && !icon.isLocked) {
        const angle = Math.atan2(e.clientY - iconRotateStart.cy, e.clientX - iconRotateStart.cx) * (180 / Math.PI);
        let newRotation = angle - iconRotateStart.angle;
        newRotation = ((newRotation % 360) + 360) % 360;
        setElementIcons(prev => prev.map(i => 
          i.id === rotatingIconId ? { ...i, rotation: Math.round(newRotation) } : i
        ));
      }
    }
    
    // Handle Element Icon resize
    if (resizingIconId) {
      const icon = elementIcons.find(i => i.id === resizingIconId);
      if (icon && !icon.isLocked) {
        const deltaY = iconResizeStart.y - e.clientY; // Drag up to make bigger
        const newSize = Math.max(30, Math.min(120, iconResizeStart.size + deltaY));
        setElementIcons(prev => prev.map(i => 
          i.id === resizingIconId ? { ...i, size: Math.round(newSize) } : i
        ));
      }
    }
  }, [movingCompassId, moveStart, compasses, updateCompass, isMovingDoor, doorMoveStart, mainDoor, isMovingNorth, northMoveStart, northMarker, isMovingWater, waterMoveStart, waterElement, isMovingWood, woodMoveStart, woodElement, isMovingFire, fireMoveStart, fireElement, isMovingEarth, earthMoveStart, earthElement, isMovingMetal, metalMoveStart, metalElement, movingLabelId, labelMoveStart, movingIconId, iconMoveStart, isRotatingDoor, doorRotateStart, isResizingDoor, doorResizeStart, rotatingIconId, iconRotateStart, resizingIconId, iconResizeStart, elementIcons, screenToCanvas]);

  const handleCompassMouseUp = useCallback(() => {
    setMovingCompassId(null);
    setIsMovingDoor(false);
    setIsMovingNorth(false);
    setIsMovingWater(false);
    setIsMovingWood(false);
    setIsMovingFire(false);
    setIsMovingEarth(false);
    setIsMovingMetal(false);
    setMovingLabelId(null);
    setMovingIconId(null);
    setIsRotatingDoor(false);
    setIsResizingDoor(false);
    setRotatingIconId(null);
    setResizingIconId(null);
  }, []);

  // Rotation change handler
  const handleRotationChange = useCallback((id, rotation) => {
    const compass = compasses.find(c => c.id === id);
    if (!compass || compass.isLocked) return;
    updateCompass(id, { rotation });
  }, [compasses, updateCompass]);

  // Export functionality
  const handleExport = useCallback(async () => {
    if (!exportRef.current) return;

    try {
      toast.loading('Exporting image...');
      
      const srcCanvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 1,
        useCORS: true,
        logging: false,
        allowTaint: true,
      });

      let finalCanvas = srcCanvas;

      // Ensure horizontal (landscape) format
      if (srcCanvas.height > srcCanvas.width) {
        finalCanvas = document.createElement('canvas');
        finalCanvas.width = srcCanvas.height;
        finalCanvas.height = srcCanvas.width;
        const ctx = finalCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
        ctx.translate(finalCanvas.width / 2, finalCanvas.height / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.drawImage(srcCanvas, -srcCanvas.width / 2, -srcCanvas.height / 2);
      }

      const link = document.createElement('a');
      link.download = `fengshui-analysis-${Date.now()}.png`;
      link.href = finalCanvas.toDataURL('image/png');
      link.click();

      toast.dismiss();
      toast.success('Image exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.dismiss();
      toast.error('Export failed');
    }
  }, []);

  // Save functionality - show dialog first
  const handleSave = useCallback(() => {
    setProfileName('');
    setShowOverwriteConfirm(false);
    setShowSaveDialog(true);
  }, []);

  // Actually execute the save (called directly for new projects, or after overwrite confirmation)
  const executeSave = useCallback(async () => {
    const trimmedName = profileName.trim();
    if (!trimmedName) return;

    // Get floor plan image from canvas (may be large base64)
    const floorPlanImage = floorPlanRef.current?.getImage() || null;

    const projectData = {
      name: trimmedName,
      compasses,
      mainDoor,
      elementIcons,
      textLabels,
      floorPlanImage,
      floorPlanRotation,
      centerBox: confirmedBox,
      showCenterLines,
    };

    try {
      // If user is authenticated, save to backend
      if (isAuthenticated && user) {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          throw new Error('Not authenticated. Please login again.');
        }
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(projectData),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Server error: ${response.status}`);
        }

        // Refresh projects list
        const projectsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (projectsResponse.ok) {
          const projects = await projectsResponse.json();
          setSavedProfiles(projects);
        }

        setCurrentProjectName(trimmedName);
        setShowSaveDialog(false);
        setShowOverwriteConfirm(false);
        toast.success(`Project "${trimmedName}" saved to cloud!`);
      } else {
        // Fallback to localStorage for non-authenticated users
        const existingProfiles = JSON.parse(localStorage.getItem('fengshui-profiles') || '[]');
        const existingIndex = existingProfiles.findIndex(p => p.name === trimmedName);
        
        const localProject = { ...projectData, savedAt: new Date().toISOString() };
        
        if (existingIndex >= 0) {
          existingProfiles[existingIndex] = localProject;
        } else {
          existingProfiles.push(localProject);
        }
        
        localStorage.setItem('fengshui-profiles', JSON.stringify(existingProfiles));
        localStorage.setItem('fengshui-current-profile', trimmedName);
        setCurrentProjectName(trimmedName);
        setSavedProfiles(existingProfiles);
        setShowSaveDialog(false);
        setShowOverwriteConfirm(false);
        toast.success(`Project "${trimmedName}" saved locally!`);
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Save failed - ' + error.message);
    }
  }, [profileName, compasses, mainDoor, elementIcons, textLabels, floorPlanRotation, confirmedBox, showCenterLines, isAuthenticated, user]);

  // Check if overwrite is needed, then save or show confirmation
  const handleConfirmSave = useCallback(() => {
    if (!profileName.trim()) {
      toast.error('Please enter a profile name');
      return;
    }

    // Check if project with same name already exists
    const existingProject = savedProfiles.find(p => p.name === profileName.trim());
    if (existingProject) {
      setShowOverwriteConfirm(true);
      return;
    }

    // New project - save directly
    executeSave();
  }, [profileName, savedProfiles, executeSave]);

  // Load a specific project
  const handleLoadProject = useCallback(async (project) => {
    try {
      let fullProject = project;

      // Fetch full project data (including floorPlanImage) from backend
      if (isAuthenticated && project.id) {
        const token = localStorage.getItem('auth_token');
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (resp.ok) {
          fullProject = await resp.json();
        }
      }

      // Load compasses
      if (fullProject.compasses && fullProject.compasses.length > 0) {
        setCompasses(fullProject.compasses);
        setSelectedCompassId(fullProject.compasses[0].id);
      }
      
      // Load main door
      if (fullProject.mainDoor) {
        setMainDoor(fullProject.mainDoor);
      } else {
        setMainDoor(null);
      }
      
      // Load element icons
      if (fullProject.elementIcons) {
        setElementIcons(fullProject.elementIcons);
      } else {
        setElementIcons([]);
      }
      
      // Load text labels
      if (fullProject.textLabels) {
        setTextLabels(fullProject.textLabels);
      } else {
        setTextLabels([]);
      }
      
      // Load floor plan rotation
      if (fullProject.floorPlanRotation !== undefined) {
        setFloorPlanRotation(fullProject.floorPlanRotation);
      }
      
      // Load center box
      if (fullProject.centerBox) {
        setConfirmedBox(fullProject.centerBox);
      }
      
      setShowCenterLines(fullProject.showCenterLines ?? true);
      setCurrentProjectName(fullProject.name);
      setShowProjectDropdown(false);
      setImageLoaded(true);
      setIsPlotterMode(true);
      
      // Load floor plan image if available
      if (fullProject.floorPlanImage && floorPlanRef.current) {
        setTimeout(() => {
          floorPlanRef.current?.loadImage(fullProject.floorPlanImage);
        }, 100);
      }
      
      toast.success(`Loaded "${fullProject.name}"`);
    } catch (e) {
      console.error('Failed to load project:', e);
      toast.error('Failed to load project');
    }
  }, [isAuthenticated]);

  // Delete a project
  const handleDeleteProject = useCallback(async (e, project) => {
    e.stopPropagation(); // Prevent loading the project when clicking delete
    
    const projectName = typeof project === 'string' ? project : project.name;
    const projectId = typeof project === 'object' ? project.id : null;
    
    if (!window.confirm(`Are you sure you want to delete "${projectName}"?`)) {
      return;
    }
    
    try {
      // If user is authenticated and project has ID, delete from backend
      if (isAuthenticated && projectId) {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete from server');
        }
        
        // Refresh projects list
        const projectsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (projectsResponse.ok) {
          const projects = await projectsResponse.json();
          setSavedProfiles(projects);
        }
        
        toast.success(`Deleted "${projectName}" from cloud`);
      } else {
        // Fallback to localStorage
        const existingProfiles = JSON.parse(localStorage.getItem('fengshui-profiles') || '[]');
        const updatedProfiles = existingProfiles.filter(p => p.name !== projectName);
        localStorage.setItem('fengshui-profiles', JSON.stringify(updatedProfiles));
        setSavedProfiles(updatedProfiles);
        toast.success(`Deleted "${projectName}"`);
      }
      
      // If we deleted the current project, reset the entire dashboard
      if (currentProjectName === projectName) {
        setCurrentProjectName('');
        setShowProjectDropdown(false);
        setCompasses([]);
        setSelectedCompassId(null);
        setMainDoor(null);
        setIsDoorSelected(false);
        setNorthMarker(null);
        setWaterElement(null);
        setWoodElement(null);
        setFireElement(null);
        setEarthElement(null);
        setMetalElement(null);
        setElementIcons([]);
        setTextLabels([]);
        setFloorPlanRotation(0);
        setConfirmedBox(null);
        setCenterBox(null);
        setShowCenterLines(true);
        setDoorFacingDegree('');
        setKuaAnalysisEnabled(false);
        setFloorPlanZoom(100);
        setFloorPlanOffset({ x: 0, y: 0 });
        if (floorPlanRef.current?.clearImage) {
          floorPlanRef.current.clearImage();
        }
      }
    } catch (e) {
      console.error('Failed to delete project:', e);
      toast.error('Failed to delete project');
    }
  }, [currentProjectName, isAuthenticated]);

  // Create new project: reset dashboard, save empty project, set as active
  const handleCreateNewProject = useCallback(async () => {
    const trimmedName = newProjectName.trim();
    if (!trimmedName) {
      toast.error('Please enter a project name');
      return;
    }

    // Check if name already exists
    if (savedProfiles.find(p => p.name === trimmedName)) {
      toast.error('A project with this name already exists');
      return;
    }

    // Reset all dashboard state
    setCompasses([]);
    setSelectedCompassId(null);
    setMainDoor(null);
    setIsDoorSelected(false);
    setNorthMarker(null);
    setWaterElement(null);
    setWoodElement(null);
    setFireElement(null);
    setEarthElement(null);
    setMetalElement(null);
    setElementIcons([]);
    setTextLabels([]);
    setFloorPlanRotation(0);
    setConfirmedBox(null);
    setCenterBox(null);
    setShowCenterLines(true);
    setDoorFacingDegree('');
    setKuaAnalysisEnabled(false);
    setFloorPlanZoom(100);
    setFloorPlanOffset({ x: 0, y: 0 });
    // Clear the floor plan image
    if (floorPlanRef.current?.clearImage) {
      floorPlanRef.current.clearImage();
    }

    // Save empty project to backend
    const projectData = {
      name: trimmedName,
      compasses: [],
      mainDoor: null,
      elementIcons: [],
      textLabels: [],
      floorPlanImage: null,
      floorPlanRotation: 0,
      centerBox: null,
      showCenterLines: true,
    };

    try {
      if (isAuthenticated && user) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const response = await fetch(`${BACKEND_URL}/api/projects`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(projectData),
          });

          if (response.ok) {
            // Refresh projects list
            const projectsRes = await fetch(`${BACKEND_URL}/api/projects`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (projectsRes.ok) {
              setSavedProfiles(await projectsRes.json());
            }
          }
        }
      } else {
        // localStorage fallback
        const existing = JSON.parse(localStorage.getItem('fengshui-profiles') || '[]');
        existing.push({ ...projectData, savedAt: new Date().toISOString() });
        localStorage.setItem('fengshui-profiles', JSON.stringify(existing));
        setSavedProfiles(existing);
      }

      setCurrentProjectName(trimmedName);
      setShowNewProjectDialog(false);
      setNewProjectName('');
      setShowProjectDropdown(false);
      toast.success(`New project "${trimmedName}" created!`);
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  }, [newProjectName, savedProfiles, isAuthenticated, user]);

  // Load saved project and profiles - fetch from API when authenticated
  // Also auto-migrate localStorage projects to backend on first login
  React.useEffect(() => {
    // Don't load until auth check is complete
    if (authLoading) return;
    
    const loadProjects = async () => {
      try {
        // If authenticated, fetch from backend
        if (isAuthenticated && user) {
          const token = localStorage.getItem('auth_token');
          if (!token) {
            console.log('No auth token found');
            return;
          }
          
          console.log('Fetching projects from backend...');
          const response = await fetch(`${BACKEND_URL}/api/projects`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (response.ok) {
            let projects = await response.json();
            console.log('Loaded projects from backend:', projects.length);
            
            // Auto-migrate: if backend has no projects, check localStorage for old saves
            const localProfiles = JSON.parse(localStorage.getItem('fengshui-profiles') || '[]');
            if (projects.length === 0 && localProfiles.length > 0) {
              console.log(`Migrating ${localProfiles.length} local project(s) to cloud...`);
              for (const localProject of localProfiles) {
                try {
                  await fetch(`${BACKEND_URL}/api/projects`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                      name: localProject.name,
                      compasses: localProject.compasses || [],
                      mainDoor: localProject.mainDoor || null,
                      elementIcons: localProject.elementIcons || [],
                      textLabels: localProject.textLabels || [],
                      floorPlanImage: localProject.floorPlanImage || null,
                      floorPlanRotation: localProject.floorPlanRotation || 0,
                      centerBox: localProject.centerBox || null,
                      showCenterLines: localProject.showCenterLines ?? true,
                    }),
                  });
                  console.log(`Migrated project: "${localProject.name}"`);
                } catch (migErr) {
                  console.error(`Failed to migrate "${localProject.name}":`, migErr);
                }
              }
              // Re-fetch after migration
              const refreshRes = await fetch(`${BACKEND_URL}/api/projects`, {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (refreshRes.ok) {
                projects = await refreshRes.json();
              }
              // Clear localStorage projects after successful migration
              localStorage.removeItem('fengshui-profiles');
              toast.success(`Migrated ${localProfiles.length} project(s) to cloud!`);
            }
            
            setSavedProfiles(projects);
            return;
          } else {
            console.error('Failed to fetch projects:', response.status);
          }
        }
        
        // Fallback to localStorage for non-authenticated users
        const profiles = JSON.parse(localStorage.getItem('fengshui-profiles') || '[]');
        setSavedProfiles(profiles);
        
        // Load current profile name
        const currentProfile = localStorage.getItem('fengshui-current-profile');
        if (currentProfile) {
          setProfileName(currentProfile);
        }
      } catch (e) {
        console.error('Failed to load projects:', e);
        // Fallback to localStorage
        const profiles = JSON.parse(localStorage.getItem('fengshui-profiles') || '[]');
        setSavedProfiles(profiles);
      }
    };
    
    loadProjects();
  }, [isAuthenticated, user, authLoading]);

  // Reset functionality
  const handleReset = useCallback(() => {
    if (canvasRef.current) {
      const containerWidth = canvasRef.current.clientWidth;
      const containerHeight = canvasRef.current.clientHeight;
      const newCompass = {
        id: generateId(),
        position: { x: containerWidth / 2, y: containerHeight / 2 },
        rotation: 0,
        size: 550,
        opacity: 0.75,
        isLocked: false,
        isVisible: true,
      };
      setCompasses([newCompass]);
      setSelectedCompassId(newCompass.id);
    }
    setShowCenterLines(true);
    localStorage.removeItem('fengshui-project');
    toast.success('Reset complete!');
  }, []);

  // Panel drag handlers
  const handlePanelMouseDown = useCallback((e) => {
    // Only start dragging if clicking on the header area
    if (e.target.closest('.panel-drag-handle')) {
      e.preventDefault();
      setIsDraggingPanel(true);
      setPanelDragStart({
        x: e.clientX - panelPosition.x,
        y: e.clientY - panelPosition.y,
      });
    }
  }, [panelPosition]);

  const handlePanelMouseMove = useCallback((e) => {
    if (!isDraggingPanel) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 200, e.clientX - panelDragStart.x));
    const newY = Math.max(60, Math.min(window.innerHeight - 100, e.clientY - panelDragStart.y));
    setPanelPosition({ x: newX, y: newY });
  }, [isDraggingPanel, panelDragStart]);

  const handlePanelMouseUp = useCallback(() => {
    setIsDraggingPanel(false);
  }, []);

  // Add global mouse listeners for panel dragging
  React.useEffect(() => {
    if (isDraggingPanel) {
      window.addEventListener('mousemove', handlePanelMouseMove);
      window.addEventListener('mouseup', handlePanelMouseUp);
      return () => {
        window.removeEventListener('mousemove', handlePanelMouseMove);
        window.removeEventListener('mouseup', handlePanelMouseUp);
      };
    }
  }, [isDraggingPanel, handlePanelMouseMove, handlePanelMouseUp]);

  // Handle page navigation
  const handleNavigate = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="app-loading">
        <Compass size={48} className="loading-icon" />
        <p>Loading...</p>
      </div>
    );
  }

  // Auto-redirect to pricing page if returning from Stripe with session_id
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('session_id') && currentPage !== 'pricing') {
    setCurrentPage('pricing');
  }

  // Render login page
  if (currentPage === 'login') {
    return <LoginPage onNavigate={handleNavigate} />;
  }

  // Render register page
  if (currentPage === 'register') {
    return <RegisterPage onNavigate={handleNavigate} />;
  }

  // Render pricing page
  if (currentPage === 'pricing') {
    return <PricingPage onNavigate={handleNavigate} />;
  }

  // Render admin dashboard
  if (currentPage === 'admin' && isAdmin) {
    return <AdminDashboard onBack={() => handleNavigate('home')} />;
  }

  return (
    <div className="app-container" data-testid="app-container">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="header" data-testid="header">
        <div className="header-left">
          <Compass className="header-icon" />
          <div>
            <h1 className="header-title">24 Mountains Compass</h1>
            <p className="header-subtitle">Feng Shui Direction Plotter</p>
          </div>
        </div>
        
        {/* Mobile Auth Button - Top Right */}
        <div className="mobile-header-auth">
          {isAuthenticated ? (
            <div className="mobile-header-user">
              {isAdmin && (
                <button 
                  className="mobile-header-admin-btn"
                  onClick={() => handleNavigate('admin')}
                  data-testid="mobile-admin-btn"
                >
                  <Shield size={16} />
                </button>
              )}
              <button 
                className="mobile-header-logout-btn"
                onClick={logout}
                data-testid="mobile-logout-btn"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button 
              className="mobile-header-login-btn"
              onClick={() => handleNavigate('login')}
              data-testid="mobile-login-btn"
            >
              <User size={16} />
              Login
            </button>
          )}
        </div>
        
        <nav className="header-nav">
          {!imageLoaded && !isPlotterMode && (
            <>
              <a href="#upload" className="nav-link active">
                <Upload size={16} />
                Upload
              </a>
              <button 
                onClick={() => {
                  // Enter plotter mode
                  setIsPlotterMode(true);
                  setImageLoaded(true);
                  // Scroll to canvas
                  setTimeout(() => {
                    const canvas = document.querySelector('[data-testid="canvas-wrapper"]');
                    if (canvas) {
                      canvas.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="nav-link nav-link-primary"
              >
                <Compass size={16} />
                Enter Plotter
              </button>
            </>
          )}
          
          {/* Auth Buttons */}
          {imageLoaded && (
            <div className="header-hint">
              <Settings2 size={14} />
              <span>Drag compass to move • Drag edge to rotate • Click to select</span>
            </div>
          )}
          
          <div className="auth-nav-buttons">
            <button 
              className="nav-link"
              onClick={() => handleNavigate('pricing')}
              data-testid="pricing-nav-btn"
              style={{ color: '#eab308', fontWeight: '500' }}
            >
              <Star size={16} />
              Pricing
            </button>
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <button 
                    className="nav-link admin-link"
                    onClick={() => handleNavigate('admin')}
                    data-testid="admin-nav-btn"
                  >
                    <Shield size={16} />
                    Admin
                  </button>
                )}
                <div className="user-menu">
                  <span className="user-name-display">
                    <User size={14} />
                    {user?.name}
                  </span>
                  <button 
                    className="logout-btn"
                    onClick={logout}
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} />
                  </button>
                </div>
              </>
            ) : (
              <button 
                className="nav-link login-link"
                onClick={() => handleNavigate('login')}
                data-testid="login-nav-btn"
              >
                <User size={16} />
                Login
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* Project Selector Bar - Show in dashboard */}
      {imageLoaded && (
        <div className="project-selector-bar">
          <div className="project-selector-left">
            <span className="project-label">Project:</span>
            <div className="project-dropdown-wrapper">
              <button 
                className="project-dropdown-btn"
                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
              >
                <span>{currentProjectName || 'My Projects'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              
              {showProjectDropdown && (
                <div className="project-dropdown-menu">
                  <div className="project-dropdown-header">
                    <span>Saved Projects</span>
                    <button 
                      className="project-new-btn"
                      data-testid="new-project-btn"
                      onClick={() => {
                        setShowProjectDropdown(false);
                        setNewProjectName('');
                        setShowNewProjectDialog(true);
                      }}
                    >
                      + New
                    </button>
                  </div>
                  {savedProfiles.length > 0 ? (
                    <div className="project-dropdown-list">
                      {savedProfiles.map((project, idx) => (
                        <div
                          key={idx}
                          className={`project-dropdown-item ${currentProjectName === project.name ? 'active' : ''}`}
                        >
                          <div 
                            className="project-item-clickable"
                            onClick={() => handleLoadProject(project)}
                          >
                            <div className="project-item-info">
                              <span className="project-item-name">{project.name}</span>
                              <span className="project-item-date">
                                {new Date(project.updatedAt || project.savedAt || project.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            {currentProjectName === project.name && (
                              <span className="project-item-check">✓</span>
                            )}
                          </div>
                          <button 
                            className="project-item-delete"
                            onClick={(e) => handleDeleteProject(e, project)}
                            title="Delete project"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="project-dropdown-empty">
                      No saved projects yet
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="project-selector-right">
            <button 
              className="project-action-btn" 
              data-testid="undo-btn"
              onClick={handleUndo} 
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
              style={{ opacity: canUndo ? 1 : 0.4 }}
            >
              <Undo2 size={14} />
              Undo
            </button>
            <button 
              className="project-action-btn" 
              data-testid="redo-btn"
              onClick={handleRedo} 
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
              style={{ opacity: canRedo ? 1 : 0.4 }}
            >
              <Redo2 size={14} />
              Redo
            </button>
            <button className="project-action-btn" data-testid="save-btn" onClick={() => { if (!requirePro('Save Project')) return; handleSave(); }} style={{ opacity: isPro ? 1 : 0.5 }}>
              <Download size={14} />
              Save
              {!isPro && <span style={{ background: 'linear-gradient(135deg, #eab308, #f59e0b)', color: '#0f172a', padding: '0 4px', borderRadius: '4px', fontSize: '8px', fontWeight: '700', marginLeft: '4px' }}>PRO</span>}
            </button>
            <button className="project-action-btn" data-testid="export-btn" onClick={handleExport}>
              <Upload size={14} />
              Export
            </button>
          </div>
        </div>
      )}

      {/* Hero Section - Only show when no image and not in plotter mode */}
      {!imageLoaded && !isPlotterMode && (
        <section className="hero-section" data-testid="hero-section">
          <h2 className="hero-title">
            Upload Your Floor Plan for
            <span className="hero-highlight"> Feng Shui Analysis</span>
          </h2>
          <p className="hero-description">
            Overlay the 24 Mountains Luo Pan compass on your floor plan. 
            Find the center point, align directions, and analyze your space using traditional Feng Shui principles.
          </p>
          
          {/* How It Works - 4 Steps */}
          <div className="how-it-works-section">
            <div className="steps-row">
              <div className="step-card">
                <div className="step-number">1</div>
                <h4 className="step-title">Upload</h4>
                <p className="step-description">Add your floor plan image</p>
              </div>
              
              <div className="step-arrow">→</div>
              
              <div className="step-card">
                <div className="step-number">2</div>
                <h4 className="step-title">Center</h4>
                <p className="step-description">Heart of Feng Shui analysis</p>
              </div>
              
              <div className="step-arrow">→</div>
              
              <div className="step-card">
                <div className="step-number">3</div>
                <h4 className="step-title">Align</h4>
                <p className="step-description">Rotate compass to North</p>
              </div>
              
              <div className="step-arrow">→</div>
              
              <div className="step-card">
                <div className="step-number">4</div>
                <h4 className="step-title">Export</h4>
                <p className="step-description">Save or share your analysis</p>
              </div>
            </div>
          </div>
          
          {/* Mobile Enter Plotter Button */}
          <button 
            className="mobile-enter-plotter-btn"
            onClick={() => {
              setIsPlotterMode(true);
              setImageLoaded(true);
            }}
            data-testid="mobile-enter-plotter-btn"
          >
            Enter Plotter
          </button>
        </section>
      )}

      {/* Main Content - Show when in plotter mode or image loaded */}
      {(imageLoaded || isPlotterMode) && (
      <main 
        className={`main-content ${imageLoaded ? 'with-image' : ''}`}
        onMouseUp={handleCompassMouseUp}
        onMouseLeave={handleCompassMouseUp}
        data-testid="main-content"
      >
        {/* Canvas Area - Rotates everything together */}
        <div 
          ref={exportRef}
          className={`canvas-wrapper ${imageLoaded ? 'expanded' : ''}`}
          style={{
            transform: `rotate(${floorPlanRotation}deg)`,
            transformOrigin: 'center center',
          }}
          onMouseMove={handleCompassMouseMove}
          onMouseUp={handleCompassMouseUp}
          onClick={(e) => {
            // Deselect door when clicking on empty canvas area
            if (e.target === e.currentTarget || e.target.closest('.floor-plan-container')) {
              setIsDoorSelected(false);
            }
          }}
          data-testid="canvas-wrapper"
        >
          <FloorPlanCanvas
            ref={floorPlanRef}
            canvasRef={canvasRef}
            onImageLoad={handleImageLoad}
            showCenterLines={showCenterLines && imageLoaded}
            floorPlanRotation={0}
            onZoomChange={setFloorPlanZoom}
            onLockChange={setIsFloorPlanLocked}
            onOffsetChange={handleFloorPlanOffsetChange}
            centerBox={centerBox}
            confirmedBox={confirmedBox}
            isDrawingCenterBox={isDrawingCenterBox}
            onCenterBoxChange={handleCenterBoxChange}
            onDrawingComplete={handleDrawingComplete}
          />

          {/* Multiple Luo Pan Compass Overlays */}
          {imageLoaded && compasses.map((compass) => (
            compass.isVisible && (
              <div
                key={compass.id}
                className={`compass-overlay ${compass.id === selectedCompassId ? 'selected' : ''} ${compass.isLocked ? 'locked' : ''}`}
                style={{
                  left: compass.position.x - compass.size / 2,
                  top: compass.position.y - compass.size / 2,
                  cursor: isDrawingCenterBox ? 'crosshair' : (compass.isLocked ? 'not-allowed' : (movingCompassId === compass.id ? 'grabbing' : 'grab')),
                  zIndex: compass.id === selectedCompassId ? 10 : 5,
                  pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
                  transform: floorPlanRotation ? `rotate(${-floorPlanRotation}deg)` : undefined,
                }}
                onClick={() => {
                  setSelectedCompassId(compass.id);
                  setIsDoorSelected(false); // Deselect door when compass is clicked
                }}
                data-testid={`compass-overlay-${compass.id}`}
              >
                {/* Lock/Unlock indicator badge */}
                <div 
                  className={`compass-lock-badge ${compass.isLocked ? 'locked' : 'unlocked'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleLock(compass.id);
                  }}
                  title={compass.isLocked ? 'Click to unlock' : 'Click to lock'}
                >
                  {compass.isLocked ? <Lock size={14} /> : <Unlock size={14} />}
                </div>
                
                <LuoPanCompass
                  rotation={compass.rotation}
                  opacity={compass.opacity}
                  size={compass.size}
                  onRotationChange={(rotation) => handleRotationChange(compass.id, rotation)}
                  isDragging={movingCompassId === compass.id}
                  onDragStart={() => !compass.isLocked && setMovingCompassId(compass.id)}
                  onDragEnd={() => setMovingCompassId(null)}
                  onMoveStart={(e) => handleCompassMouseDown(e, compass.id)}
                  isLocked={compass.isLocked}
                  showFlyingStar={compass.showFlyingStar}
                  flyingStarScale={compass.flyingStarScale || 1}
                  highlightedDirections={compass.highlightedDirections || []}
                  showCompassOnly={compass.showCompassVisual !== false}
                  kuaAnalysisEnabled={kuaAnalysisEnabled}
                  kuaDirections={kuaDirections}
                />
              </div>
            )
          ))}

          {/* Main Door Marker */}
          {mainDoor && (
            <div
              className={`main-door-marker ${isDoorSelected ? 'selected' : ''}`}
              style={{
                left: mainDoor.isLocked && initialDoorOffset 
                  ? initialDoorOffset.x + floorPlanOffset.x 
                  : mainDoor.position.x,
                top: mainDoor.isLocked && initialDoorOffset 
                  ? initialDoorOffset.y + floorPlanOffset.y 
                  : mainDoor.position.y,
                cursor: isDrawingCenterBox ? 'crosshair' : (mainDoor.isLocked ? 'not-allowed' : (isMovingDoor ? 'grabbing' : 'grab')),
                transform: `translate(-50%, -50%) rotate(${mainDoor.rotation || 0}deg)`,
                opacity: mainDoor.opacity ?? 1,
                '--door-size': `${mainDoor.size || 40}px`,
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
              }}
              onMouseDown={handleDoorMouseDown}
              onClick={(e) => e.stopPropagation()}
              data-testid="main-door-marker"
            >
              <div className="main-door-circle" style={{ width: `${mainDoor.size || 40}px`, height: `${mainDoor.size || 40}px` }}>
                <DoorOpen size={Math.max(16, (mainDoor.size || 40) * 0.6)} />
                {/* Rotation handle - top right */}
                {!mainDoor.isLocked && (
                  <div
                    className="door-rotation-handle"
                    onMouseDown={handleDoorRotateStart}
                    data-testid="door-rotation-handle"
                  >
                    <RotateCw size={10} />
                  </div>
                )}
                {/* Resize handle - bottom right */}
                {!mainDoor.isLocked && (
                  <div
                    className="door-resize-handle"
                    onMouseDown={handleDoorResizeStart}
                    data-testid="door-resize-handle"
                  >
                    <Maximize2 size={10} />
                  </div>
                )}
              </div>
              <span className="main-door-label">MAIN DOOR</span>
              <span className="main-door-rotation-label">{Math.round(mainDoor.rotation || 0)}°</span>
            </div>
          )}

          {/* North Direction Marker */}
          {northMarker && (
            <div
              className="north-marker"
              style={{
                left: northMarker.position.x,
                top: northMarker.position.y,
                cursor: isDrawingCenterBox ? 'crosshair' : (northMarker.isLocked ? 'not-allowed' : (isMovingNorth ? 'grabbing' : 'grab')),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
              }}
              onMouseDown={handleNorthMouseDown}
              data-testid="north-marker"
            >
              <div className="north-circle">
                <span className="north-letter">N</span>
              </div>
              <span className="north-label">NORTH</span>
              <div className="north-arrow"></div>
            </div>
          )}

          {/* Water Element Marker */}
          {waterElement && (
            <div
              className="water-element-marker"
              style={{
                left: waterElement.position.x,
                top: waterElement.position.y,
                cursor: isDrawingCenterBox ? 'crosshair' : (waterElement.isLocked ? 'not-allowed' : (isMovingWater ? 'grabbing' : 'grab')),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
                '--water-size': `${waterElement.size || 50}px`,
                transform: `translate(-50%, -50%) rotate(${waterElement.rotation || 0}deg)`,
              }}
              onMouseDown={handleWaterMouseDown}
              data-testid="water-element-marker"
            >
              <div className="water-element-circle" style={{ width: `${waterElement.size || 50}px`, height: `${waterElement.size || 50}px` }}>
                <svg viewBox="0 0 100 100" width="100%" height="100%">
                  {/* Gold outer ring */}
                  <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                  {/* Inner cream circle */}
                  <circle cx="50" cy="50" r="42" fill="#fef9e7"/>
                  {/* Water waves background */}
                  <ellipse cx="50" cy="60" rx="38" ry="28" fill="#1e88e5"/>
                  {/* Wave 1 - left */}
                  <path d="M15 45 Q22 35 28 45 Q34 55 28 60 Q22 65 18 55 Q14 48 15 45" fill="#2196f3" stroke="#ffffff" strokeWidth="2"/>
                  <circle cx="24" cy="38" r="6" fill="#2196f3" stroke="#ffffff" strokeWidth="1.5"/>
                  {/* Wave 2 - center */}
                  <path d="M38 42 Q48 30 58 42 Q68 55 58 65 Q48 70 42 60 Q36 50 38 42" fill="#42a5f5" stroke="#ffffff" strokeWidth="2"/>
                  <circle cx="48" cy="32" r="7" fill="#42a5f5" stroke="#ffffff" strokeWidth="1.5"/>
                  {/* Wave 3 - right */}
                  <path d="M65 45 Q72 35 78 45 Q84 55 78 62 Q72 68 68 58 Q64 50 65 45" fill="#64b5f6" stroke="#ffffff" strokeWidth="2"/>
                  <circle cx="73" cy="38" r="5" fill="#64b5f6" stroke="#ffffff" strokeWidth="1.5"/>
                  {/* Water foam highlights */}
                  <path d="M20 70 Q30 62 40 68 Q50 74 60 68 Q70 62 80 70" fill="none" stroke="#90caf9" strokeWidth="3" strokeLinecap="round"/>
                  <path d="M25 78 Q40 72 55 78 Q70 84 78 75" fill="none" stroke="#bbdefb" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <span 
                className="water-element-label"
                style={{
                  marginTop: `${(waterElement.size || 50) * 0.12}px`,
                  padding: `${(waterElement.size || 50) * 0.08}px ${(waterElement.size || 50) * 0.24}px`,
                  fontSize: `${(waterElement.size || 50) * 0.22}px`,
                  borderRadius: `${(waterElement.size || 50) * 0.24}px`,
                }}
              >WATER</span>
            </div>
          )}

          {/* Wood Element Marker */}
          {woodElement && (
            <div
              className="wood-element-marker"
              style={{
                left: woodElement.position.x,
                top: woodElement.position.y,
                cursor: isDrawingCenterBox ? 'crosshair' : (woodElement.isLocked ? 'not-allowed' : (isMovingWood ? 'grabbing' : 'grab')),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
                '--wood-size': `${woodElement.size || 50}px`,
                transform: `translate(-50%, -50%) rotate(${woodElement.rotation || 0}deg)`,
              }}
              onMouseDown={handleWoodMouseDown}
              data-testid="wood-element-marker"
            >
              <div className="wood-element-circle" style={{ width: `${woodElement.size || 50}px`, height: `${woodElement.size || 50}px` }}>
                <svg viewBox="0 0 100 100" width="100%" height="100%">
                  {/* Gold outer ring */}
                  <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                  {/* Inner cream circle */}
                  <circle cx="50" cy="50" r="42" fill="#fef9e7"/>
                  {/* Pot - celadon style */}
                  <ellipse cx="50" cy="82" rx="22" ry="8" fill="#6b8e7a"/>
                  <path d="M28 75 Q28 88 50 88 Q72 88 72 75 Q72 72 50 72 Q28 72 28 75" fill="#8fbc8f"/>
                  <ellipse cx="50" cy="73" rx="22" ry="6" fill="#a8d5a2"/>
                  <ellipse cx="42" cy="80" rx="4" ry="2" fill="#ffffff" opacity="0.4"/>
                  {/* Bamboo stalk 1 - left */}
                  <rect x="32" y="30" width="8" height="42" fill="#4a7c59" rx="2"/>
                  <line x1="32" y1="40" x2="40" y2="40" stroke="#3d6b4a" strokeWidth="2"/>
                  <line x1="32" y1="50" x2="40" y2="50" stroke="#3d6b4a" strokeWidth="2"/>
                  <line x1="32" y1="60" x2="40" y2="60" stroke="#3d6b4a" strokeWidth="2"/>
                  {/* Bamboo stalk 2 - center */}
                  <rect x="46" y="22" width="8" height="50" fill="#5a8f6a" rx="2"/>
                  <line x1="46" y1="32" x2="54" y2="32" stroke="#4a7c59" strokeWidth="2"/>
                  <line x1="46" y1="44" x2="54" y2="44" stroke="#4a7c59" strokeWidth="2"/>
                  <line x1="46" y1="56" x2="54" y2="56" stroke="#4a7c59" strokeWidth="2"/>
                  {/* Bamboo stalk 3 - right */}
                  <rect x="60" y="35" width="8" height="37" fill="#4a7c59" rx="2"/>
                  <line x1="60" y1="45" x2="68" y2="45" stroke="#3d6b4a" strokeWidth="2"/>
                  <line x1="60" y1="55" x2="68" y2="55" stroke="#3d6b4a" strokeWidth="2"/>
                  {/* Bamboo leaves */}
                  <path d="M30 30 Q22 22 18 28 Q24 26 30 30" fill="#2d5a3d"/>
                  <path d="M32 28 Q28 18 22 22 Q28 22 32 28" fill="#3d6b4a"/>
                  <path d="M40 32 Q48 22 44 18 Q42 24 40 32" fill="#2d5a3d"/>
                  <path d="M46 22 Q42 12 48 10 Q48 16 46 22" fill="#3d6b4a"/>
                  <path d="M54 24 Q62 16 58 12 Q56 18 54 24" fill="#2d5a3d"/>
                  <path d="M52 20 Q56 10 62 14 Q56 16 52 20" fill="#4a7c59"/>
                  <path d="M60 35 Q68 28 72 32 Q66 32 60 35" fill="#2d5a3d"/>
                  <path d="M68 38 Q76 32 78 38 Q72 36 68 38" fill="#3d6b4a"/>
                </svg>
              </div>
              <span 
                className="wood-element-label"
                style={{
                  marginTop: `${(woodElement.size || 50) * 0.12}px`,
                  padding: `${(woodElement.size || 50) * 0.08}px ${(woodElement.size || 50) * 0.24}px`,
                  fontSize: `${(woodElement.size || 50) * 0.22}px`,
                  borderRadius: `${(woodElement.size || 50) * 0.24}px`,
                }}
              >WOOD</span>
            </div>
          )}

          {/* Fire Element Marker */}
          {fireElement && (
            <div
              className="fire-element-marker"
              style={{
                left: fireElement.position.x,
                top: fireElement.position.y,
                cursor: isDrawingCenterBox ? 'crosshair' : (fireElement.isLocked ? 'not-allowed' : (isMovingFire ? 'grabbing' : 'grab')),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
                '--fire-size': `${fireElement.size || 50}px`,
                transform: `translate(-50%, -50%) rotate(${fireElement.rotation || 0}deg)`,
              }}
              onMouseDown={handleFireMouseDown}
              data-testid="fire-element-marker"
            >
              <div className="fire-element-circle" style={{ width: `${fireElement.size || 50}px`, height: `${fireElement.size || 50}px` }}>
                <svg viewBox="0 0 100 100" width="100%" height="100%">
                  {/* Gold outer ring */}
                  <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                  {/* Inner cream circle */}
                  <circle cx="50" cy="50" r="42" fill="#fef9e7"/>
                  {/* Fire base/glow */}
                  <ellipse cx="50" cy="75" rx="20" ry="8" fill="#991b1b" opacity="0.6"/>
                  {/* Main flame - outer red */}
                  <path d="M50 20 Q30 45 35 60 Q38 72 50 78 Q62 72 65 60 Q70 45 50 20" fill="#dc2626"/>
                  {/* Inner flame - orange */}
                  <path d="M50 28 Q38 48 42 58 Q45 68 50 72 Q55 68 58 58 Q62 48 50 28" fill="#f97316"/>
                  {/* Core flame - yellow */}
                  <path d="M50 38 Q44 52 47 60 Q48 66 50 68 Q52 66 53 60 Q56 52 50 38" fill="#fbbf24"/>
                  {/* Left flame tongue */}
                  <path d="M35 55 Q28 42 32 32 Q36 42 38 50 Q40 58 35 55" fill="#dc2626"/>
                  {/* Right flame tongue */}
                  <path d="M65 55 Q72 42 68 32 Q64 42 62 50 Q60 58 65 55" fill="#dc2626"/>
                  {/* Highlight */}
                  <path d="M46 45 Q44 50 46 55" fill="none" stroke="#fef3c7" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
                </svg>
              </div>
              <span 
                className="fire-element-label"
                style={{
                  marginTop: `${(fireElement.size || 50) * 0.12}px`,
                  padding: `${(fireElement.size || 50) * 0.08}px ${(fireElement.size || 50) * 0.24}px`,
                  fontSize: `${(fireElement.size || 50) * 0.22}px`,
                  borderRadius: `${(fireElement.size || 50) * 0.24}px`,
                }}
              >FIRE</span>
            </div>
          )}

          {/* Earth Element Marker */}
          {earthElement && (
            <div
              className="earth-element-marker"
              style={{
                left: earthElement.position.x,
                top: earthElement.position.y,
                cursor: isDrawingCenterBox ? 'crosshair' : (earthElement.isLocked ? 'not-allowed' : (isMovingEarth ? 'grabbing' : 'grab')),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
                '--earth-size': `${earthElement.size || 50}px`,
                transform: `translate(-50%, -50%) rotate(${earthElement.rotation || 0}deg)`,
              }}
              onMouseDown={handleEarthMouseDown}
              data-testid="earth-element-marker"
            >
              <div className="earth-element-circle" style={{ width: `${earthElement.size || 50}px`, height: `${earthElement.size || 50}px` }}>
                <svg viewBox="0 0 100 100" width="100%" height="100%">
                  {/* Gold outer ring */}
                  <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                  {/* Inner cream/yellow circle */}
                  <circle cx="50" cy="50" r="42" fill="#fef3c7"/>
                  {/* Mountain 1 - main/center (tallest) */}
                  <path d="M50 20 L20 85 L80 85 Z" fill="#92400e"/>
                  <path d="M50 20 L50 85 L80 85 Z" fill="#78350f"/>
                  {/* Snow cap 1 */}
                  <path d="M50 20 L38 42 Q44 48 50 42 Q56 48 62 42 L50 20" fill="#ffffff"/>
                  <path d="M38 42 Q32 50 28 48" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round"/>
                  <path d="M62 42 Q68 50 72 48" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round"/>
                  {/* Mountain 2 - left (smaller) */}
                  <path d="M25 45 L8 85 L55 85 Z" fill="#a16207" opacity="0.9"/>
                  {/* Snow cap 2 */}
                  <path d="M25 45 L18 58 Q22 62 25 58 Q28 62 32 58 L25 45" fill="#f8fafc"/>
                  {/* Mountain 3 - right (smaller) */}
                  <path d="M75 50 L55 85 L92 85 Z" fill="#b45309" opacity="0.85"/>
                  {/* Snow cap 3 */}
                  <path d="M75 50 L68 62 Q72 66 75 62 Q78 66 82 62 L75 50" fill="#e5e7eb"/>
                </svg>
              </div>
              <span 
                className="earth-element-label"
                style={{
                  marginTop: `${(earthElement.size || 50) * 0.12}px`,
                  padding: `${(earthElement.size || 50) * 0.08}px ${(earthElement.size || 50) * 0.24}px`,
                  fontSize: `${(earthElement.size || 50) * 0.22}px`,
                  borderRadius: `${(earthElement.size || 50) * 0.24}px`,
                }}
              >EARTH</span>
            </div>
          )}

          {/* Metal Element Marker */}
          {metalElement && (
            <div
              className="metal-element-marker"
              style={{
                left: metalElement.position.x,
                top: metalElement.position.y,
                cursor: isDrawingCenterBox ? 'crosshair' : (metalElement.isLocked ? 'not-allowed' : (isMovingMetal ? 'grabbing' : 'grab')),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
                '--metal-size': `${metalElement.size || 50}px`,
                transform: `translate(-50%, -50%) rotate(${metalElement.rotation || 0}deg)`,
              }}
              onMouseDown={handleMetalMouseDown}
              data-testid="metal-element-marker"
            >
              <div className="metal-element-circle" style={{ width: `${metalElement.size || 50}px`, height: `${metalElement.size || 50}px` }}>
                <svg viewBox="0 0 100 100" width="100%" height="100%">
                  {/* Silver outer ring */}
                  <circle cx="50" cy="50" r="48" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="4"/>
                  {/* Inner light gray circle */}
                  <circle cx="50" cy="50" r="42" fill="#f9fafb"/>
                  {/* Silver ingot base (sycee/yuanbao shape) */}
                  <ellipse cx="50" cy="70" rx="32" ry="10" fill="#6b7280"/>
                  <path d="M18 70 Q18 55 30 50 L70 50 Q82 55 82 70 Q82 75 50 75 Q18 75 18 70" fill="#d1d5db"/>
                  <path d="M30 50 Q35 42 50 40 Q65 42 70 50" fill="#e5e7eb"/>
                  {/* Shine effects */}
                  <ellipse cx="40" cy="58" rx="8" ry="4" fill="#ffffff" opacity="0.8"/>
                  {/* Chinese coin (round with square hole) */}
                  <circle cx="50" cy="35" r="18" fill="#9ca3af" stroke="#6b7280" strokeWidth="2"/>
                  <circle cx="50" cy="35" r="14" fill="#d1d5db"/>
                  <rect x="44" y="29" width="12" height="12" fill="#6b7280"/>
                  {/* Coin shine */}
                  <path d="M38 28 Q42 24 48 26" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
                  {/* Small coins */}
                  <circle cx="28" cy="52" r="8" fill="#9ca3af" stroke="#6b7280" strokeWidth="1"/>
                  <rect x="25" y="49" width="6" height="6" fill="#6b7280"/>
                  <circle cx="72" cy="52" r="8" fill="#9ca3af" stroke="#6b7280" strokeWidth="1"/>
                  <rect x="69" y="49" width="6" height="6" fill="#6b7280"/>
                </svg>
              </div>
              <span 
                className="metal-element-label"
                style={{
                  marginTop: `${(metalElement.size || 50) * 0.12}px`,
                  padding: `${(metalElement.size || 50) * 0.08}px ${(metalElement.size || 50) * 0.24}px`,
                  fontSize: `${(metalElement.size || 50) * 0.22}px`,
                  borderRadius: `${(metalElement.size || 50) * 0.24}px`,
                }}
              >METAL</span>
            </div>
          )}

          {/* Element Icons (Multiple Instances) */}
          {elementIcons.map(icon => (
            <div
              key={icon.id}
              className={`${icon.type.toLowerCase()}-element-marker`}
              style={{
                position: 'absolute',
                left: icon.position.x,
                top: icon.position.y,
                transform: `translate(-50%, -50%) rotate(${icon.rotation || 0}deg)`,
                cursor: icon.isLocked ? 'not-allowed' : (movingIconId === icon.id ? 'grabbing' : 'grab'),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
                zIndex: 50,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
              onMouseDown={(e) => handleElementIconMouseDown(e, icon)}
              data-testid={`element-icon-${icon.id}`}
            >
              <div className={`${icon.type.toLowerCase()}-element-circle`} style={{ width: `${icon.size || 50}px`, height: `${icon.size || 50}px`, position: 'relative', overflow: 'visible' }}>
                {icon.type === 'WATER' && (
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                    <circle cx="50" cy="50" r="42" fill="#fef9e7"/>
                    <ellipse cx="50" cy="60" rx="38" ry="28" fill="#1e88e5"/>
                    <path d="M15 45 Q22 35 28 45 Q34 55 28 60 Q22 65 18 55 Q14 48 15 45" fill="#2196f3" stroke="#ffffff" strokeWidth="2"/>
                    <circle cx="24" cy="38" r="6" fill="#2196f3" stroke="#ffffff" strokeWidth="1.5"/>
                    <path d="M38 42 Q48 30 58 42 Q68 55 58 65 Q48 70 42 60 Q36 50 38 42" fill="#42a5f5" stroke="#ffffff" strokeWidth="2"/>
                    <circle cx="48" cy="32" r="7" fill="#42a5f5" stroke="#ffffff" strokeWidth="1.5"/>
                    <path d="M65 45 Q72 35 78 45 Q84 55 78 62 Q72 68 68 58 Q64 50 65 45" fill="#64b5f6" stroke="#ffffff" strokeWidth="2"/>
                    <circle cx="73" cy="38" r="5" fill="#64b5f6" stroke="#ffffff" strokeWidth="1.5"/>
                    <path d="M20 70 Q30 62 40 68 Q50 74 60 68 Q70 62 80 70" fill="none" stroke="#90caf9" strokeWidth="3" strokeLinecap="round"/>
                    <path d="M25 78 Q40 72 55 78 Q70 84 78 75" fill="none" stroke="#bbdefb" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
                {icon.type === 'WOOD' && (
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                    <circle cx="50" cy="50" r="42" fill="#fef9e7"/>
                    {/* Pot - celadon style */}
                    <ellipse cx="50" cy="82" rx="22" ry="8" fill="#6b8e7a"/>
                    <path d="M28 75 Q28 88 50 88 Q72 88 72 75 Q72 72 50 72 Q28 72 28 75" fill="#8fbc8f"/>
                    <ellipse cx="50" cy="73" rx="22" ry="6" fill="#a8d5a2"/>
                    <ellipse cx="42" cy="80" rx="4" ry="2" fill="#ffffff" opacity="0.4"/>
                    {/* Bamboo stalk 1 - left */}
                    <rect x="32" y="30" width="8" height="42" fill="#4a7c59" rx="2"/>
                    <line x1="32" y1="40" x2="40" y2="40" stroke="#3d6b4a" strokeWidth="2"/>
                    <line x1="32" y1="50" x2="40" y2="50" stroke="#3d6b4a" strokeWidth="2"/>
                    <line x1="32" y1="60" x2="40" y2="60" stroke="#3d6b4a" strokeWidth="2"/>
                    {/* Bamboo stalk 2 - center */}
                    <rect x="46" y="22" width="8" height="50" fill="#5a8f6a" rx="2"/>
                    <line x1="46" y1="32" x2="54" y2="32" stroke="#4a7c59" strokeWidth="2"/>
                    <line x1="46" y1="44" x2="54" y2="44" stroke="#4a7c59" strokeWidth="2"/>
                    <line x1="46" y1="56" x2="54" y2="56" stroke="#4a7c59" strokeWidth="2"/>
                    {/* Bamboo stalk 3 - right */}
                    <rect x="60" y="35" width="8" height="37" fill="#4a7c59" rx="2"/>
                    <line x1="60" y1="45" x2="68" y2="45" stroke="#3d6b4a" strokeWidth="2"/>
                    <line x1="60" y1="55" x2="68" y2="55" stroke="#3d6b4a" strokeWidth="2"/>
                    {/* Bamboo leaves */}
                    <path d="M30 30 Q22 22 18 28 Q24 26 30 30" fill="#2d5a3d"/>
                    <path d="M32 28 Q28 18 22 22 Q28 22 32 28" fill="#3d6b4a"/>
                    <path d="M40 32 Q48 22 44 18 Q42 24 40 32" fill="#2d5a3d"/>
                    <path d="M46 22 Q42 12 48 10 Q48 16 46 22" fill="#3d6b4a"/>
                    <path d="M54 24 Q62 16 58 12 Q56 18 54 24" fill="#2d5a3d"/>
                    <path d="M52 20 Q56 10 62 14 Q56 16 52 20" fill="#4a7c59"/>
                    <path d="M60 35 Q68 28 72 32 Q66 32 60 35" fill="#2d5a3d"/>
                    <path d="M68 38 Q76 32 78 38 Q72 36 68 38" fill="#3d6b4a"/>
                  </svg>
                )}
                {icon.type === 'FIRE' && (
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                    <circle cx="50" cy="50" r="42" fill="#fef9e7"/>
                    <path d="M50 15 Q65 35 60 50 Q70 40 65 60 Q75 55 68 75 Q60 90 50 88 Q40 90 32 75 Q25 55 35 60 Q30 40 40 50 Q35 35 50 15" fill="#ff5722"/>
                    <path d="M50 25 Q60 40 55 52 Q62 45 58 65 Q50 80 50 78 Q50 80 42 65 Q38 45 45 52 Q40 40 50 25" fill="#ff9800"/>
                    <path d="M50 40 Q55 50 52 60 Q50 70 50 68 Q50 70 48 60 Q45 50 50 40" fill="#ffeb3b"/>
                  </svg>
                )}
                {icon.type === 'EARTH' && (
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    <circle cx="50" cy="50" r="48" fill="#f5e6a3" stroke="#c9a227" strokeWidth="4"/>
                    <circle cx="50" cy="50" r="42" fill="#fef3c7"/>
                    <path d="M50 20 L20 85 L80 85 Z" fill="#92400e"/>
                    <path d="M50 20 L50 85 L80 85 Z" fill="#78350f"/>
                    <path d="M50 20 L38 42 Q44 48 50 42 Q56 48 62 42 L50 20" fill="#ffffff"/>
                    <path d="M38 42 Q32 50 28 48" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round"/>
                    <path d="M62 42 Q68 50 72 48" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round"/>
                    <path d="M25 45 L8 85 L55 85 Z" fill="#a16207" opacity="0.9"/>
                    <path d="M25 45 L18 58 Q22 62 25 58 Q28 62 32 58 L25 45" fill="#f8fafc"/>
                    <path d="M75 50 L55 85 L92 85 Z" fill="#b45309" opacity="0.85"/>
                    <path d="M75 50 L68 62 Q72 66 75 62 Q78 66 82 62 L75 50" fill="#e5e7eb"/>
                  </svg>
                )}
                {icon.type === 'METAL' && (
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    <circle cx="50" cy="50" r="48" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="4"/>
                    <circle cx="50" cy="50" r="42" fill="#f9fafb"/>
                    <ellipse cx="50" cy="70" rx="32" ry="10" fill="#6b7280"/>
                    <path d="M18 70 Q18 55 30 50 L70 50 Q82 55 82 70 Q82 75 50 75 Q18 75 18 70" fill="#d1d5db"/>
                    <path d="M30 50 Q35 42 50 40 Q65 42 70 50" fill="#e5e7eb"/>
                    <ellipse cx="40" cy="58" rx="8" ry="4" fill="#ffffff" opacity="0.8"/>
                    <circle cx="50" cy="35" r="18" fill="#9ca3af" stroke="#6b7280" strokeWidth="2"/>
                    <circle cx="50" cy="35" r="14" fill="#d1d5db"/>
                    <rect x="44" y="29" width="12" height="12" fill="#6b7280"/>
                    <path d="M38 28 Q42 24 48 26" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="28" cy="52" r="8" fill="#9ca3af" stroke="#6b7280" strokeWidth="1"/>
                    <rect x="25" y="49" width="6" height="6" fill="#6b7280"/>
                    <circle cx="72" cy="52" r="8" fill="#9ca3af" stroke="#6b7280" strokeWidth="1"/>
                    <rect x="69" y="49" width="6" height="6" fill="#6b7280"/>
                  </svg>
                )}
                {/* Rotation handle */}
                {!icon.isLocked && (
                  <div
                    className="icon-rotation-handle"
                    onMouseDown={(e) => handleIconRotateStart(e, icon.id)}
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: `${Math.max(16, (icon.size || 50) * 0.3)}px`,
                      height: `${Math.max(16, (icon.size || 50) * 0.3)}px`,
                      background: '#2563eb',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'grab',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      zIndex: 100,
                    }}
                  >
                    <RotateCw size={Math.max(10, (icon.size || 50) * 0.18)} color="white" />
                  </div>
                )}
                {/* Resize handle */}
                {!icon.isLocked && (
                  <div
                    className="icon-resize-handle"
                    onMouseDown={(e) => handleIconResizeStart(e, icon.id, icon.size)}
                    style={{
                      position: 'absolute',
                      bottom: '-8px',
                      right: '-8px',
                      width: `${Math.max(16, (icon.size || 50) * 0.3)}px`,
                      height: `${Math.max(16, (icon.size || 50) * 0.3)}px`,
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'ns-resize',
                      boxShadow: '0 2px 4px rgba(16,185,129,0.4)',
                      zIndex: 100,
                      border: '2px solid white',
                    }}
                    data-testid={`icon-resize-handle-${icon.id}`}
                  >
                    <Maximize2 size={Math.max(8, (icon.size || 50) * 0.16)} color="white" />
                  </div>
                )}
              </div>
              <span 
                className={`${icon.type.toLowerCase()}-element-label`}
                style={{
                  marginTop: `${(icon.size || 50) * 0.12}px`,
                  padding: `${(icon.size || 50) * 0.08}px ${(icon.size || 50) * 0.24}px`,
                  fontSize: `${(icon.size || 50) * 0.22}px`,
                  borderRadius: `${(icon.size || 50) * 0.24}px`,
                }}
              >
                {icon.name.toUpperCase()}
              </span>
              {/* Rotation degree label */}
              <span style={{
                fontSize: `${(icon.size || 50) * 0.18}px`,
                color: '#6b7280',
                marginTop: '2px',
              }}>
                {Math.round(icon.rotation || 0)}°
              </span>
            </div>
          ))}

          {/* Text Labels */}
          {textLabels.map(label => (
            <div
              key={label.id}
              className="text-label-marker"
              style={{
                left: label.position.x,
                top: label.position.y,
                transform: `translate(-50%, -50%) rotate(${label.rotation || 0}deg)`,
                cursor: label.isLocked ? 'not-allowed' : (movingLabelId === label.id ? 'grabbing' : 'grab'),
                pointerEvents: isDrawingCenterBox ? 'none' : 'auto',
              }}
              onMouseDown={(e) => handleLabelMouseDown(e, label)}
              data-testid={`text-label-${label.id}`}
            >
              <span 
                className="text-label-text"
                style={{ 
                  fontSize: `${label.size || 18}px`,
                  backgroundColor: label.color,
                }}
              >
                {label.text}
              </span>
            </div>
          ))}
        </div>

        {/* Compass List Panel - Draggable */}
        {imageLoaded && (
          <div 
            className={`compass-list-panel ${leftPanelExpanded ? 'expanded' : ''}`}
            data-testid="compass-list-panel"
            style={{
              position: 'absolute',
              left: panelPosition.x,
              top: panelPosition.y,
              cursor: isDraggingPanel ? 'grabbing' : 'default',
            }}
            onMouseDown={handlePanelMouseDown}
          >
            {/* Mobile Toggle Handle */}
            <div 
              className="mobile-panel-toggle"
              onClick={() => setLeftPanelExpanded(!leftPanelExpanded)}
            >
              <span>{leftPanelExpanded ? 'Hide Controls' : 'Show Controls'}</span>
            </div>
            
            {/* Drag Handle */}
            <div className="panel-drag-handle">
              <GripVertical size={14} />
              <span>Controls</span>
              <GripVertical size={14} />
            </div>
            
            {/* Floor Plan Controls Section */}
            <div className="floor-plan-section">
              <div className="compass-list-header">
                <span>Floor Plan</span>
                <div
                  style={{
                    padding: '4px 10px',
                    backgroundColor: isFloorPlanLocked ? '#9ca3af' : '#2563eb',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#ffffff',
                  }}
                >
                  {floorPlanZoom}%
                </div>
              </div>
              <div className="floor-plan-controls-row">
                <button
                  className="floor-ctrl-btn"
                  onClick={() => floorPlanRef.current?.zoomIn()}
                  disabled={isFloorPlanLocked}
                  title="Zoom In"
                >
                  <ZoomIn size={16} />
                </button>
                <button
                  className="floor-ctrl-btn"
                  onClick={() => floorPlanRef.current?.zoomOut()}
                  disabled={isFloorPlanLocked}
                  title="Zoom Out"
                >
                  <ZoomOut size={16} />
                </button>
                <button
                  className="floor-ctrl-btn"
                  onClick={() => floorPlanRef.current?.resetView()}
                  disabled={isFloorPlanLocked}
                  title="Reset View"
                >
                  <RotateCcw size={16} />
                </button>
                <button
                  className={`floor-ctrl-btn ${isFloorPlanLocked ? 'locked' : ''}`}
                  onClick={() => floorPlanRef.current?.toggleLock()}
                  title={isFloorPlanLocked ? "Unlock Floor Plan" : "Lock Floor Plan"}
                >
                  {isFloorPlanLocked ? <Lock size={16} /> : <Unlock size={16} />}
                </button>
                <button
                  className="floor-ctrl-btn center"
                  onClick={() => floorPlanRef.current?.handleCenterFloorPlan()}
                  disabled={isFloorPlanLocked}
                  title="Center Floor Plan"
                >
                  <Crosshair size={16} />
                </button>
                <button
                  className="floor-ctrl-btn delete"
                  onClick={() => floorPlanRef.current?.clearImage()}
                  disabled={isFloorPlanLocked}
                  title="Remove Floor Plan"
                >
                  <X size={16} />
                </button>
              </div>
              
              {/* Reset All Button - Always visible on dashboard */}
              <button
                className="reset-all-btn"
                onClick={handleResetAll}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '8px',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                data-testid="reset-all-btn"
              >
                <RotateCcw size={14} />
                Reset All (Start Fresh)
              </button>
            </div>

            <div className="compass-list-header" style={{ borderTop: '1px solid var(--border-color)' }}>
              <span>Compasses ({compasses.length})</span>
              <button 
                className="add-compass-btn"
                onClick={handleAddCompass}
                title="Add new compass"
                data-testid="add-compass-btn"
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="compass-list">
              {compasses.map((compass, index) => (
                <div 
                  key={compass.id}
                  className={`compass-list-item ${compass.id === selectedCompassId ? 'selected' : ''}`}
                  onClick={() => setSelectedCompassId(compass.id)}
                >
                  <span className="compass-name">Compass {index + 1}</span>
                  <div className="compass-item-actions">
                    <button
                      className={`star-btn ${compass.showFlyingStar ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        updateCompass(compass.id, { showFlyingStar: !compass.showFlyingStar });
                        toast.success(compass.showFlyingStar ? 'Flying Star hidden' : 'Flying Star shown');
                      }}
                      title={compass.showFlyingStar ? 'Hide Flying Star' : 'Show Flying Star'}
                    >
                      <Star size={12} />
                    </button>
                    <button
                      className="center-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCenterCompass(compass.id);
                      }}
                      title="Snap to center"
                      data-testid="snap-to-center-btn"
                    >
                      <Crosshair size={12} />
                    </button>
                    <button
                      className={`lock-btn ${compass.isLocked ? 'locked' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleLock(compass.id);
                      }}
                      title={compass.isLocked ? 'Unlock' : 'Lock'}
                    >
                      {compass.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                    </button>
                    {compasses.length > 1 && (
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCompass(compass.id);
                        }}
                        title="Delete compass"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Main Door Section */}
            <div className="main-door-section">
              <div className="compass-list-header" style={{ borderTop: '1px solid var(--border-color)' }}>
                <span>Main Door</span>
              </div>
              {mainDoor ? (
                <>
                  <div className="compass-list-item main-door-item">
                    <span className="compass-name">
                      <DoorOpen size={14} style={{ marginRight: '6px', color: mainDoor.isLocked ? '#dc2626' : '#2563eb' }} />
                      Door Marker {mainDoor.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button
                        className={`lock-btn ${mainDoor.isLocked ? 'locked' : ''}`}
                        onClick={handleToggleDoorLock}
                        title={mainDoor.isLocked ? 'Unlock Door' : 'Lock Door'}
                        data-testid="door-lock-btn"
                      >
                        {mainDoor.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button
                        className="delete-btn"
                        onClick={handleRemoveMainDoor}
                        title="Remove Main Door"
                        disabled={mainDoor.isLocked}
                        style={{ opacity: mainDoor.isLocked ? 0.5 : 1, cursor: mainDoor.isLocked ? 'not-allowed' : 'pointer' }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <button 
                  className="add-main-door-btn"
                  onClick={handleAddMainDoor}
                  data-testid="add-main-door-btn"
                >
                  <DoorOpen size={16} />
                  <span>Add Main Door</span>
                </button>
              )}
            </div>
            
            {/* Five Elements Section - Combined */}
            <div className="elements-section">
              <div className="compass-list-header" style={{ borderTop: '1px solid var(--border-color)' }}>
                <span>Five Elements 五行</span>
              </div>
              
              {/* Added Elements */}
              {waterElement && (
                <div className="element-control-item water-item">
                  <div className="element-header">
                    <span className="compass-name">
                      <span className="element-icon" style={{ background: '#2563eb' }}>水</span>
                      Water {waterElement.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button className={`lock-btn ${waterElement.isLocked ? 'locked' : ''}`} onClick={handleToggleWaterLock} title={waterElement.isLocked ? 'Unlock' : 'Lock'}>
                        {waterElement.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button className="delete-btn" onClick={handleRemoveWaterElement} title="Remove" disabled={waterElement.isLocked} style={{ opacity: waterElement.isLocked ? 0.5 : 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {woodElement && (
                <div className="element-control-item wood-item">
                  <div className="element-header">
                    <span className="compass-name">
                      <span className="element-icon" style={{ background: '#16a34a' }}>木</span>
                      Wood {woodElement.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button className={`lock-btn ${woodElement.isLocked ? 'locked' : ''}`} onClick={handleToggleWoodLock} title={woodElement.isLocked ? 'Unlock' : 'Lock'}>
                        {woodElement.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button className="delete-btn" onClick={handleRemoveWoodElement} title="Remove" disabled={woodElement.isLocked} style={{ opacity: woodElement.isLocked ? 0.5 : 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {fireElement && (
                <div className="element-control-item fire-item">
                  <div className="element-header">
                    <span className="compass-name">
                      <span className="element-icon" style={{ background: '#dc2626' }}>火</span>
                      Fire {fireElement.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button className={`lock-btn ${fireElement.isLocked ? 'locked' : ''}`} onClick={handleToggleFireLock} title={fireElement.isLocked ? 'Unlock' : 'Lock'}>
                        {fireElement.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button className="delete-btn" onClick={handleRemoveFireElement} title="Remove" disabled={fireElement.isLocked} style={{ opacity: fireElement.isLocked ? 0.5 : 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {earthElement && (
                <div className="element-control-item earth-item">
                  <div className="element-header">
                    <span className="compass-name">
                      <span className="element-icon" style={{ background: '#92400e' }}>土</span>
                      Earth {earthElement.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button className={`lock-btn ${earthElement.isLocked ? 'locked' : ''}`} onClick={handleToggleEarthLock} title={earthElement.isLocked ? 'Unlock' : 'Lock'}>
                        {earthElement.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button className="delete-btn" onClick={handleRemoveEarthElement} title="Remove" disabled={earthElement.isLocked} style={{ opacity: earthElement.isLocked ? 0.5 : 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {metalElement && (
                <div className="element-control-item metal-item">
                  <div className="element-header">
                    <span className="compass-name">
                      <span className="element-icon" style={{ background: '#6b7280' }}>金</span>
                      Metal {metalElement.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button className={`lock-btn ${metalElement.isLocked ? 'locked' : ''}`} onClick={handleToggleMetalLock} title={metalElement.isLocked ? 'Unlock' : 'Lock'}>
                        {metalElement.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button className="delete-btn" onClick={handleRemoveMetalElement} title="Remove" disabled={metalElement.isLocked} style={{ opacity: metalElement.isLocked ? 0.5 : 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Add Element Buttons - Icons & Text Combined */}
              <div className="add-elements-row combined" style={{ position: 'relative' }}>
                {!isPro && (
                  <div 
                    data-testid="elements-pro-gate"
                    onClick={() => requirePro('Five Elements')}
                    style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', borderRadius: '8px', backdropFilter: 'blur(2px)' }}
                  >
                    <span style={{ background: 'linear-gradient(135deg, #eab308, #f59e0b)', color: '#0f172a', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>PRO</span>
                  </div>
                )}
                <div className="add-elements-grid">
                  {/* Icons */}
                  <button className="add-element-btn water" onClick={handleAddWaterElement} data-testid="add-water-btn" title="Add Water Icon">
                    <span className="element-icon" style={{ background: '#2563eb' }}>水</span>
                  </button>
                  <button className="add-element-btn wood" onClick={handleAddWoodElement} data-testid="add-wood-btn" title="Add Wood Icon">
                    <span className="element-icon" style={{ background: '#16a34a' }}>木</span>
                  </button>
                  <button className="add-element-btn fire" onClick={handleAddFireElement} data-testid="add-fire-btn" title="Add Fire Icon">
                    <span className="element-icon" style={{ background: '#dc2626' }}>火</span>
                  </button>
                  <button className="add-element-btn earth" onClick={handleAddEarthElement} data-testid="add-earth-btn" title="Add Earth Icon">
                    <span className="element-icon" style={{ background: '#92400e' }}>土</span>
                  </button>
                  <button className="add-element-btn metal" onClick={handleAddMetalElement} data-testid="add-metal-btn" title="Add Metal Icon">
                    <span className="element-icon" style={{ background: '#6b7280' }}>金</span>
                  </button>
                  {/* Text */}
                  <button className="add-text-btn water" onClick={() => handleAddTextLabel('WATER')} title="Add WATER text">
                    <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '7px' }}>W</span>
                  </button>
                  <button className="add-text-btn wood" onClick={() => handleAddTextLabel('WOOD')} title="Add WOOD text">
                    <span style={{ color: '#16a34a', fontWeight: 'bold', fontSize: '7px' }}>Wo</span>
                  </button>
                  <button className="add-text-btn fire" onClick={() => handleAddTextLabel('FIRE')} title="Add FIRE text">
                    <span style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '7px' }}>F</span>
                  </button>
                  <button className="add-text-btn earth" onClick={() => handleAddTextLabel('EARTH')} title="Add EARTH text">
                    <span style={{ color: '#92400e', fontWeight: 'bold', fontSize: '7px' }}>E</span>
                  </button>
                  <button className="add-text-btn metal" onClick={() => handleAddTextLabel('METAL')} title="Add METAL text">
                    <span style={{ color: '#6b7280', fontWeight: 'bold', fontSize: '7px' }}>M</span>
                  </button>
                </div>
              </div>

              {/* Element Icons Controls */}
              {elementIcons.map(icon => (
                <div key={icon.id} className="element-control-item" style={{ borderLeft: `3px solid ${icon.color}` }}>
                  <div className="element-header">
                    <span className="compass-name">
                      {icon.name} {icon.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button className={`lock-btn ${icon.isLocked ? 'locked' : ''}`} onClick={() => handleToggleElementIconLock(icon.id)} title={icon.isLocked ? 'Unlock' : 'Lock'}>
                        {icon.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button className="delete-btn" onClick={() => handleRemoveElementIcon(icon.id)} title="Remove" disabled={icon.isLocked} style={{ opacity: icon.isLocked ? 0.5 : 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Text Labels Controls */}
              {textLabels.map(label => (
                <div key={label.id} className="element-control-item" style={{ borderLeft: `3px solid ${label.color}` }}>
                  <div className="element-header">
                    <span className="compass-name" style={{ color: label.color, fontWeight: 'bold' }}>
                      {label.text} {label.isLocked && <Lock size={10} style={{ marginLeft: '4px', color: '#dc2626' }} />}
                    </span>
                    <div className="compass-item-actions">
                      <button className={`lock-btn ${label.isLocked ? 'locked' : ''}`} onClick={() => handleToggleLabelLock(label.id)} title={label.isLocked ? 'Unlock' : 'Lock'}>
                        {label.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button className="delete-btn" onClick={() => handleRemoveTextLabel(label.id)} title="Remove" disabled={label.isLocked} style={{ opacity: label.isLocked ? 0.5 : 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="element-controls">
                    <div className="element-slider">
                      <span>Size</span>
                      <input type="range" min="10" max="48" value={label.size || 16} onChange={(e) => handleUpdateLabelSize(label.id, e.target.value)} disabled={label.isLocked} />
                      <span>{label.size || 16}</span>
                    </div>
                    <div className="element-slider">
                      <span>Rotate</span>
                      <input type="range" min="0" max="360" value={label.rotation || 0} onChange={(e) => handleUpdateLabelRotation(label.id, e.target.value)} disabled={label.isLocked} />
                      <span>{label.rotation || 0}°</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rotation Display for selected compass */}
        {selectedCompass && imageLoaded && (
          <div className="rotation-badge" data-testid="rotation-display">
            <span className="rotation-degree">{Math.round(selectedCompass.rotation)}°</span>
            {directionDisplayMode === '8dir' ? (
              <span className="rotation-direction">{getDirectionName(selectedCompass.rotation)}</span>
            ) : (
              <span className="rotation-direction rotation-24mtn">
                {get24MountainName(selectedCompass.rotation)}
              </span>
            )}
            <button 
              className="direction-mode-toggle"
              onClick={() => setDirectionDisplayMode(prev => prev === '8dir' ? '24mtn' : '8dir')}
              title={directionDisplayMode === '8dir' ? 'Switch to 24 Mountains' : 'Switch to 8 Directions'}
            >
              {directionDisplayMode === '8dir' ? '8方' : '24山'}
            </button>
            {selectedCompass.isLocked && (
              <span className="locked-indicator">
                <Lock size={12} /> Locked
              </span>
            )}
          </div>
        )}
      </main>
      )}

      {/* Control Panel - controls selected compass */}
      {imageLoaded && selectedCompass && (
        <>
          {/* Mobile Toggle for Right Panel */}
          <button 
            className="mobile-control-toggle"
            onClick={() => setRightPanelExpanded(!rightPanelExpanded)}
          >
            {rightPanelExpanded ? 'Close' : 'Settings'}
          </button>
          
          <ControlPanel
            className={rightPanelExpanded ? 'show-panel' : ''}
            rotation={selectedCompass.rotation}
            onRotationChange={(rotation) => !selectedCompass.isLocked && updateCompass(selectedCompassId, { rotation })}
            opacity={selectedCompass.opacity}
            onOpacityChange={(opacity) => updateCompass(selectedCompassId, { opacity })}
            showCenterLines={showCenterLines}
            onShowCenterLinesChange={setShowCenterLines}
            centerBox={centerBox}
            confirmedBox={confirmedBox}
            isDrawingCenterBox={isDrawingCenterBox}
            onStartDrawCenterBox={handleStartDrawCenterBox}
          onClearCenterBox={handleClearCenterBox}
          onConfirmCenterBox={handleConfirmCenterBox}
          onClearConfirmedBox={handleClearConfirmedBox}
          showCompass={selectedCompass.isVisible}
          onShowCompassChange={(isVisible) => updateCompass(selectedCompassId, { isVisible })}
          compassSize={selectedCompass.size}
          onCompassSizeChange={(size) => updateCompass(selectedCompassId, { size })}
          onExport={handleExport}
          onSave={handleSave}
          onReset={handleReset}
          isCollapsed={isPanelCollapsed}
          onToggleCollapse={() => setIsPanelCollapsed(!isPanelCollapsed)}
          isLocked={selectedCompass.isLocked}
          onToggleLock={() => handleToggleLock(selectedCompassId)}
          showFlyingStar={selectedCompass.showFlyingStar}
          onToggleFlyingStar={() => {
            if (!isPro && !selectedCompass.showFlyingStar) { requirePro('Flying Star'); return; }
            updateCompass(selectedCompassId, { showFlyingStar: !selectedCompass.showFlyingStar });
          }}
          flyingStarScale={selectedCompass.flyingStarScale || 1}
          onFlyingStarScaleChange={(scale) => updateCompass(selectedCompassId, { flyingStarScale: scale })}
          highlightedDirections={selectedCompass.highlightedDirections || []}
          onToggleHighlight={(index) => {
            if (!isPro && index >= 12) { requirePro('Highlight Directions (S1-NW3)'); return; }
            const current = selectedCompass.highlightedDirections || [];
            const newHighlights = current.includes(index)
              ? current.filter(i => i !== index)
              : [...current, index];
            updateCompass(selectedCompassId, { highlightedDirections: newHighlights });
          }}
          floorPlanRotation={floorPlanRotation}
          onFloorPlanRotationChange={setFloorPlanRotation}
          mainDoor={mainDoor}
          isDoorSelected={isDoorSelected}
          onDoorRotationChange={handleDoorRotationChange}
          onDoorOpacityChange={handleDoorOpacityChange}
          onDoorSizeChange={handleDoorSizeChange}
          showCompassVisual={selectedCompass.showCompassVisual !== false}
          onToggleCompassVisual={() => updateCompass(selectedCompassId, { showCompassVisual: !selectedCompass.showCompassVisual })}
          floorPlanZoom={floorPlanZoom}
          onFloorPlanZoomIn={() => floorPlanRef.current?.zoomIn()}
          onFloorPlanZoomOut={() => floorPlanRef.current?.zoomOut()}
          isFloorPlanLocked={isFloorPlanLocked}
          isPro={isPro}
          kuaAnalysisEnabled={kuaAnalysisEnabled}
          onKuaAnalysisToggle={handleKuaAnalysisToggle}
          birthDate={birthDate}
          onBirthDateChange={handleBirthDateChange}
          gender={gender}
          onGenderChange={handleGenderChange}
          kuaNumber={kuaNumber}
          kuaDirections={kuaDirections}
        />
        </>
      )}

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <p>24 Mountains Feng Shui Compass Tool • Built for practitioners and enthusiasts</p>
      </footer>

      {/* Save Profile Dialog */}
      {showSaveDialog && (
        <div 
          data-testid="save-dialog-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => { setShowSaveDialog(false); setShowOverwriteConfirm(false); }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '420px',
              maxWidth: '90vw',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Overwrite Confirmation View */}
            {showOverwriteConfirm ? (
              <div data-testid="overwrite-confirm-dialog">
                <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  Overwrite Project?
                </h3>
                <div style={{ 
                  padding: '12px 16px', 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  border: '1px solid #fde68a',
                }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#92400e', fontWeight: '500' }}>
                    A project named "<strong>{profileName.trim()}</strong>" already exists.
                  </p>
                  <p style={{ margin: 0, fontSize: '13px', color: '#a16207' }}>
                    This will replace the existing project data. This action cannot be undone.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    data-testid="overwrite-cancel-btn"
                    onClick={() => setShowOverwriteConfirm(false)}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '500',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#4b5563',
                    }}
                  >
                    Go Back
                  </button>
                  <button
                    data-testid="overwrite-confirm-btn"
                    onClick={executeSave}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '500',
                      backgroundColor: '#dc2626',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: 'white',
                    }}
                  >
                    Overwrite Project
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  Save Project
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
                  Enter a new name or select an existing project to update
                </p>
                
                {/* Searchable Input with Dropdown */}
                <div style={{ position: 'relative', marginBottom: '16px' }}>
                  <input
                    data-testid="save-project-name-input"
                    type="text"
                    value={profileName}
                    onChange={(e) => { setProfileName(e.target.value); setShowOverwriteConfirm(false); }}
                    placeholder="Search or enter new project name..."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => setTimeout(() => e.target.style.borderColor = '#e5e7eb', 200)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirmSave()}
                    autoFocus
                  />
                  
                  {/* Dropdown list of existing profiles */}
                  {savedProfiles.length > 0 && (
                    <div 
                      style={{
                        marginTop: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                      }}
                    >
                      <div style={{ padding: '8px 12px', fontSize: '11px', color: '#9ca3af', fontWeight: '500', textTransform: 'uppercase', borderBottom: '1px solid #f3f4f6' }}>
                        Saved Projects ({savedProfiles.filter(p => 
                          p.name.toLowerCase().includes(profileName.toLowerCase())
                        ).length})
                      </div>
                      {savedProfiles
                        .filter(p => p.name.toLowerCase().includes(profileName.toLowerCase()))
                        .map((profile, idx) => (
                          <div
                            key={idx}
                            data-testid={`saved-project-item-${idx}`}
                            onClick={() => setProfileName(profile.name)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor: profileName === profile.name ? '#eff6ff' : 'transparent',
                              borderBottom: '1px solid #f3f4f6',
                              transition: 'background-color 0.15s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = profileName === profile.name ? '#eff6ff' : 'transparent'}
                          >
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                                {profile.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                Saved: {new Date(profile.updatedAt || profile.savedAt || profile.createdAt).toLocaleDateString()} {new Date(profile.updatedAt || profile.savedAt || profile.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            </div>
                            {profileName === profile.name && (
                              <div style={{ 
                                width: '20px', 
                                height: '20px', 
                                borderRadius: '50%', 
                                backgroundColor: '#2563eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                              }}>
                                ✓
                              </div>
                            )}
                          </div>
                        ))}
                      {savedProfiles.filter(p => p.name.toLowerCase().includes(profileName.toLowerCase())).length === 0 && (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                          No matching projects found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {profileName.trim() && !savedProfiles.find(p => p.name === profileName.trim()) && (
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f0fdf4', 
                    borderRadius: '6px', 
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#166534',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{ fontSize: '16px' }}>+</span>
                    New project "<strong>{profileName.trim()}</strong>" will be created
                  </div>
                )}

                {profileName.trim() && savedProfiles.find(p => p.name === profileName.trim()) && (
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#fef3c7', 
                    borderRadius: '6px', 
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    Project "<strong>{profileName.trim()}</strong>" exists - will ask to confirm overwrite
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    data-testid="save-cancel-btn"
                    onClick={() => { setShowSaveDialog(false); setShowOverwriteConfirm(false); }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '500',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#4b5563',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="save-project-btn"
                    onClick={handleConfirmSave}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '500',
                      backgroundColor: '#2563eb',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: 'white',
                    }}
                  >
                    Save Project
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* New Project Dialog */}
      {showNewProjectDialog && (
        <div 
          data-testid="new-project-dialog-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowNewProjectDialog(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '400px',
              maxWidth: '90vw',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              Create New Project
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              This will reset the dashboard and start fresh
            </p>
            <input
              data-testid="new-project-name-input"
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Enter project name..."
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: '16px',
              }}
              onFocus={(e) => e.target.style.borderColor = '#2563eb'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateNewProject()}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                data-testid="new-project-cancel-btn"
                onClick={() => setShowNewProjectDialog(false)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#4b5563',
                }}
              >
                Cancel
              </button>
              <button
                data-testid="new-project-create-btn"
                onClick={handleCreateNewProject}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: '#2563eb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'white',
                }}
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="app-footer" data-testid="app-footer">
        <div className="footer-content">
          <p className="footer-title">FengShui Plotter - 24 Mountains Compass</p>
          <p className="footer-copyright">Copyright © 2026 fengshuiplotter.live. All rights reserved worldwide.</p>
        </div>
      </footer>
    </div>
  );
}

// Helper function to get direction name from rotation
// Using simplified system (NW includes NNW, up to 348.75°)
function getDirectionName(rotation) {
  const directions = [
    { min: 348.75, max: 360, name: 'North 北' },
    { min: 0, max: 11.25, name: 'North 北' },
    { min: 11.25, max: 56.25, name: 'Northeast 東北' },
    { min: 56.25, max: 101.25, name: 'East 東' },
    { min: 101.25, max: 146.25, name: 'Southeast 東南' },
    { min: 146.25, max: 191.25, name: 'South 南' },
    { min: 191.25, max: 236.25, name: 'Southwest 西南' },
    { min: 236.25, max: 281.25, name: 'West 西' },
    { min: 281.25, max: 348.75, name: 'Northwest 西北' }, // NW includes NNW
  ];

  for (const dir of directions) {
    if (rotation >= dir.min && rotation < dir.max) {
      return dir.name;
    }
  }
  return 'North 北';
}

// Helper function to get 24 Mountain name from rotation
// Based on Joey Yap's 24 Mountains degree reference
function get24MountainName(rotation) {
  const mountains = [
    { min: 337.6, max: 352.5, name: '壬', pinyin: 'Ren', subDir: 'N1' },
    { min: 352.6, max: 360, name: '子', pinyin: 'Zi', subDir: 'N2' },
    { min: 0, max: 7.5, name: '子', pinyin: 'Zi', subDir: 'N2' },
    { min: 7.6, max: 22.5, name: '癸', pinyin: 'Gui', subDir: 'N3' },
    { min: 22.6, max: 37.5, name: '丑', pinyin: 'Chou', subDir: 'NE1' },
    { min: 37.6, max: 52.5, name: '艮', pinyin: 'Gen', subDir: 'NE2' },
    { min: 52.6, max: 67.5, name: '寅', pinyin: 'Yin', subDir: 'NE3' },
    { min: 67.6, max: 82.5, name: '甲', pinyin: 'Jia', subDir: 'E1' },
    { min: 82.6, max: 97.5, name: '卯', pinyin: 'Mao', subDir: 'E2' },
    { min: 97.6, max: 112.5, name: '乙', pinyin: 'Yi', subDir: 'E3' },
    { min: 112.6, max: 127.5, name: '辰', pinyin: 'Chen', subDir: 'SE1' },
    { min: 127.6, max: 142.5, name: '巽', pinyin: 'Xun', subDir: 'SE2' },
    { min: 142.6, max: 157.5, name: '巳', pinyin: 'Si', subDir: 'SE3' },
    { min: 157.6, max: 172.5, name: '丙', pinyin: 'Bing', subDir: 'S1' },
    { min: 172.6, max: 187.5, name: '午', pinyin: 'Wu', subDir: 'S2' },
    { min: 187.6, max: 202.5, name: '丁', pinyin: 'Ding', subDir: 'S3' },
    { min: 202.6, max: 217.5, name: '未', pinyin: 'Wei', subDir: 'SW1' },
    { min: 217.6, max: 232.5, name: '坤', pinyin: 'Kun', subDir: 'SW2' },
    { min: 232.6, max: 247.5, name: '申', pinyin: 'Shen', subDir: 'SW3' },
    { min: 247.6, max: 262.5, name: '庚', pinyin: 'Geng', subDir: 'W1' },
    { min: 262.6, max: 277.5, name: '酉', pinyin: 'You', subDir: 'W2' },
    { min: 277.6, max: 292.5, name: '辛', pinyin: 'Xin', subDir: 'W3' },
    { min: 292.6, max: 307.5, name: '戌', pinyin: 'Xu', subDir: 'NW1' },
    { min: 307.6, max: 322.5, name: '乾', pinyin: 'Qian', subDir: 'NW2' },
    { min: 322.6, max: 337.5, name: '亥', pinyin: 'Hai', subDir: 'NW3' },
  ];

  for (const mtn of mountains) {
    if (rotation >= mtn.min && rotation <= mtn.max) {
      return `${mtn.name} ${mtn.pinyin} (${mtn.subDir})`;
    }
  }
  return '子 Zi (N2)';
}

export default App;
